import { format,subDays, subMinutes } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

// ✅ use sempre o singleton
import { supabaseAcor } from '@/features/sistema/services/supabaseClientAcor';

// Exporta o singleton para compatibilidade com código existente
export const supabase = supabaseAcor;

// Helpers de schema (evita nomes-tabela com 'public.' / 'acor.')
const getDbPublic = () => supabase.schema('public');
const getDbAcor = () => supabase.schema('acor');

async function executeSupabaseQuery<T = any>(builder: any): Promise<T> {
  if (builder && typeof builder.then === 'function') {
    return builder.then(
      (result: T) => result,
      (error: unknown) => Promise.reject(error)
    );
  }
  return builder as T;
}

// -----------------------------------------
// Util: normalizar data para 'YYYY-MM-DD'
// -----------------------------------------
export function toDateOnly(value?: string | null): string | undefined {
  if (!value) return undefined;
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value; // já é date-only
    const d = new Date(value);
    if (isNaN(d.getTime())) return undefined;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  } catch {
    return undefined;
  }
}

// Util: formata data YYYY-MM-DD -> DD/MM
function fmtDataBR(d?: string | null): string {
  if (!d) return "—";
  try {
    const date = new Date(d);
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  } catch {
    return "—";
  }
}

// Util: normaliza string (evita "undefined" na mensagem)
const nz = (v?: string | null, fallback = "-") => (v && String(v).trim() ? String(v).trim() : fallback);

const FALLBACK_ASSISTENTE_ID = 'd68196ad-f4a7-41cd-99bf-89400c3df890';

// Busca dados essenciais da requisição/obra/itens para montar a mensagem
async function carregarDadosParaMensagem(requisicaoId: string) {
  // 1) Requisição (schema: acor)
  const { data: req, error: errReq } = await executeSupabaseQuery(
    getDbAcor()
    .from("compras_requisicoes")
    .select("id, obra_id, tipo_solicitacao, urgencia, prazo_entrega, retirada, retirada_responsavel, numero_nota, criado_por")
    .eq("id", requisicaoId)
    .single()
  );
  if (errReq || !req) throw errReq ?? new Error("Requisição não encontrada");

  // 2) Obra (schema: public) com dados dos usuários
  const { data: obra, error: errObra } = await executeSupabaseQuery(
    getDbPublic()
    .from("obras")
    .select(`
      id, nome, endereco, tipo_obra, contratante,
      coordenador_nome, coordenador_id,
      estagiario_nome, estagiario_id,
      coordenador:coordenador_id(nome, whatsapp),
      estagiario:estagiario_id(nome, whatsapp)
    `)
    .eq("id", req.obra_id)
    .single()
  );
  if (errObra) throw errObra;

  // 3) Itens (schema: acor)
  const { data: itens, error: errItens } = await executeSupabaseQuery(
    getDbAcor()
    .from("compras_requisicao_itens")
    .select("nome_digitado, quantidade, unidade")
    .eq("requisicao_id", requisicaoId)
    .order("created_at", { ascending: true })
  );
  if (errItens) throw errItens;

  return { req, obra, itens: itens ?? [] };
}

export async function ensureThreadForRequisicaoApp(requisicaoId: string): Promise<string> {
  const tryRpc = async (name: 'ensure_thread_for_requisicao_v2' | 'ensure_thread_for_requisicao') => {
    const { data, error } = await supabase.rpc(name, { p_requisicao: requisicaoId });
    if (error) throw error;
    if (!data) throw new Error(`${name} retornou vazio para ${requisicaoId}`);
    return data as string;
  };

  try {
    return await tryRpc('ensure_thread_for_requisicao_v2');
  } catch (e) {
    console.warn('[compras] ensure_thread_for_requisicao_v2 falhou, tentando versão legada', e);
  }

  return await tryRpc('ensure_thread_for_requisicao');
}

async function comentarItensRequisicaoManual(requisicaoId: string, authorId?: string | null) {
  console.warn('[comentarItensRequisicao] ativando fallback manual (client-side).');
  const { req, obra, itens } = await carregarDadosParaMensagem(requisicaoId);

  const resolvedAuthor =
    authorId ??
    req?.criado_por ??
    obra?.coordenador_id ??
    obra?.estagiario_id ??
    null;
  if (!resolvedAuthor) {
    throw new Error('Não foi possível resolver o autor para o comentário automático.');
  }

  const threadId = await ensureThreadForRequisicaoApp(requisicaoId);

  const itensTxt = (itens ?? []).length
    ? (itens ?? [])
        .map((it: any) => {
          const quantidade = it?.quantidade ?? 1;
          const unidade = nz(it?.unidade ?? 'un', 'un');
          const nome = nz(it?.nome_digitado, '—');
          return `- ${quantidade} ${unidade} — ${nome}`;
        })
        .join('\n')
    : '—';
  const urgencia = nz(req?.urgencia, '—');
  const prazoTxt = req?.prazo_entrega ? fmtDataBR(req.prazo_entrega) : '—';

  const destinatarioNome = 'Thamires';
  const mencoes = [FALLBACK_ASSISTENTE_ID];
  const conteudo = [
    `@${destinatarioNome}, por favor dar seguimento.`,
    `Urgência: ${urgencia}`,
    `Prazo: ${prazoTxt}`,
    ``,
    `Itens (${(itens ?? []).length}):`,
    itensTxt,
  ].join('\n');

  const agora = new Date().toISOString();
  const cincoMin = subMinutes(new Date(agora), 5).toISOString();
  const { data: repetidos } = await executeSupabaseQuery(
    getDbPublic()
      .from('app_comments')
      .select('id')
      .eq('thread_id', threadId)
      .eq('created_by', resolvedAuthor)
      .eq('conteudo', conteudo)
      .gte('created_at', cincoMin)
  );
  if (repetidos && repetidos.length) {
    console.info('[comentarItensRequisicao] comentário automático já existente (dedupe).');
    return;
  }

  const insertPayload = {
    thread_id: threadId,
    conteudo,
    tipo: 'texto',
    mencoes,
    created_at: agora,
    updated_at: agora,
    created_by: resolvedAuthor,
    updated_by: resolvedAuthor,
    editado: false,
    fixado: false,
    ordem_fixacao: null,
    deleted_at: null,
  };

  const { error } = await executeSupabaseQuery(
    getDbPublic().from('app_comments').insert(insertPayload)
  );
  if (error) throw error;
  console.info('[comentarItensRequisicao] fallback manual inseriu comentário em app_comments.');
}

/**
 * Gera a mensagem para WhatsApp baseada no tipo da requisição:
 * - 'pedido'   => mensagem de pedido **com faturamento**
 * - 'orcamento' => mensagem pedindo **cotação**
 *
 * @param requisicaoId UUID da requisição
 * @param fornecedorId ID da loja/fornecedor selecionado (opcional para compor cabeçalho)
 */
export async function gerarMensagemPedido(requisicaoId: string, _fornecedorId?: string): Promise<string> {
  const { req, obra, itens } = await carregarDadosParaMensagem(requisicaoId);
  const isPedido = (req.tipo_solicitacao ?? "orcamento") === "pedido";

  // Lista dos materiais
  const linhasItens = itens.map((it: any) => {
    const q = it.quantidade ?? 1;
    const un = nz(it.unidade ?? "un", "un");
    const nome = nz(it.nome_digitado, "");
    return `- ${q} ${un} ${nome}`.trim();
  });
  const blocoItens = linhasItens.length ? linhasItens.join("\n") : "- (sem itens)";

  const entrega = req.retirada
    ? `RETIRADA no balcão\nResponsável: ${nz(req.retirada_responsavel)}`
    : `Endereço de entrega:\n${nz(obra?.endereco)}`;

  const contato = [
    obra?.coordenador ? `${obra.coordenador.nome} (Coordenador(a))${obra.coordenador.whatsapp ? ` - ${obra.coordenador.whatsapp}` : ''}` : null,
    obra?.estagiario ? `${obra.estagiario.nome} (Estagiário(a))${obra.estagiario.whatsapp ? ` - ${obra.estagiario.whatsapp}` : ''}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const dataEntrega = fmtDataBR(req.prazo_entrega);

  if (isPedido) {
    // Bloco "Faturar" — regra:
    // - Se obra.tipo_obra === 'administracao':
    //     usar obra.contratante; se vazio -> "FATURAR EM NOME DO CLIENTE"
    // - Caso contrário:
    //     manter dados da ACOR (envs VITE_EMPRESA_NOME / VITE_EMPRESA_CNPJ)
    let blocoFaturar: string;
    const isAdministracao =
      (obra?.tipo_obra ?? "").toLowerCase() === "administracao";
    if (isAdministracao) {
      const contratante = (obra?.contratante ?? "").trim();
      blocoFaturar = ["Faturar:", contratante || "FATURAR EM NOME DO CLIENTE"].join("\n");
    } else {
      const nomeEmpresa = import.meta.env.VITE_EMPRESA_NOME ?? "ACOR ENGENHARIA";
      const cnpjEmpresa = import.meta.env.VITE_EMPRESA_CNPJ ?? "36.283.804/0001-92";
      blocoFaturar = ["Faturar:", nomeEmpresa, cnpjEmpresa ? `CNPJ: ${cnpjEmpresa}` : null]
        .filter(Boolean)
        .join("\n");
    }

    // MODO PEDIDO (com faturamento)
    return [
      `Segue PEDIDO para a obra: ${nz(obra?.nome)}`,
      ``,
      `Descrição dos materiais:`,
      ``,
      blocoItens,
      ``,
      entrega,
      ``,
      blocoFaturar,
      ``,
      contato ? `Contato para entrega:` : ``,
      contato ? contato : ``,
      ``,
      `Data de entrega: ${dataEntrega}`,
      ``,
    ]
      .join("\n");
  }

  // MODO ORÇAMENTO
  return [
    `Gostaria de uma cotação para a obra: ${nz(obra?.nome)}`,
    ``,
    `Materiais:`,
    ``,
    blocoItens,
    ``,
    entrega,
    ``,
    `Solicitamos informar: preço unitário, marca/modelo, prazo de entrega, disponibilidade, condições de pagamento e frete.`,
    ``,
    `Previsão de entrega desejada: ${dataEntrega}`,
    ``,
  ]
    .join("\n");
}

/**
 * Gera mensagem de pedido para WhatsApp.
 * O histórico será registrado pelo fluxo padrão de comentários (via RPC de comentar).
 * Esta função apenas retorna o texto formatado para envio.
 */
export async function salvarPedido(requisicaoId: string, _fornecedorId: string | null | undefined, _mensagem: string): Promise<boolean> {
  // Apenas valida que a requisição existe; não registra no histórico aqui
  // O histórico será criado pelo fluxo padrão quando o usuário salvar a requisição
  // Se quiser registrar no histórico, use a mesma RPC de comentário (mas isso deve ser feito no fluxo de salvar)
  // Por enquanto, apenas retorna true indicando que a mensagem foi gerada
  console.log('[salvarPedido] Mensagem gerada para requisição:', requisicaoId);
  return true;
}

/**
 * Busca fornecedores agrupados (master + lojas) para o autosuggest da tela,
 * usando sua view acor.vw_fornecedores_cotacao.
 */
export async function buscarFornecedoresAgrupados(termo: string) {
  const { data, error } = await executeSupabaseQuery(
    getDbAcor()
    .from("vw_fornecedores_cotacao")
    .select("id, nome, filiais_count")
    .ilike("nome", `%${termo}%`)
    .order("nome")
    .limit(20)
  );
  if (error) throw error;

  // A view não lista as lojas individualmente; para a UI funcionar,
  // oferecemos ao menos a opção master (id = canônico).
  return (data ?? []).map((row: any) => ({
    id_master: row.id,
    nome_canonico: row.nome,
    lojas_count: Math.max(1, Number(row.filiais_count || 0)), // master + (possíveis) filiais
    lojas: [
      {
        id: row.id,
        nome: row.nome,
        whatsapp_grupo: null,
      },
    ],
  }));
}

type RpcFlagSnapshot = {
  enabled: boolean;
  override: boolean | null;
  envFlag: string;
  source?: string;
  timestamp: string;
  runtimeDisabled?: RpcRuntimeDisable | null;
};

type RpcRuntimeDisable = {
  reason: string;
  source?: string;
  code?: string;
  message?: string;
  timestamp: string;
};

let lastRpcFlagSnapshot: RpcFlagSnapshot | null = null;
let rpcFlagLogCount = 0;
const RPC_FLAG_LOG_LIMIT = 5;
let runtimeRpcDisabled: RpcRuntimeDisable | null = null;
let comentarItensRpcDisabled: RpcRuntimeDisable | null = null;

function publishRpcDebug(snapshot: RpcFlagSnapshot) {
  lastRpcFlagSnapshot = snapshot;
  try {
    (globalThis as any).__COMPRAS_RPC_DEBUG__ = snapshot;
    (globalThis as any).__COMPRAS_RPC_DISABLED__ = runtimeRpcDisabled;
  } catch {
    // ignore sandbox issues
  }
  if (rpcFlagLogCount < RPC_FLAG_LOG_LIMIT) {
    console.info('[compras][rpc-flag]', snapshot);
    rpcFlagLogCount += 1;
  }
}

function disableRpcAtRuntime(reason: string, source?: string, error?: any) {
  if (runtimeRpcDisabled) return;
  runtimeRpcDisabled = {
    reason,
    source,
    code: error?.code,
    message: error?.message ?? String(error ?? ''),
    timestamp: new Date().toISOString(),
  };
  console.warn('[compras][rpc-flag] runtime disable ativado', runtimeRpcDisabled);
}

function disableComentarItensRpcAtRuntime(reason: string, error?: any) {
  if (comentarItensRpcDisabled) return;
  comentarItensRpcDisabled = {
    reason,
    source: 'comentarItensRequisicao',
    code: error?.code,
    message: error?.message ?? String(error ?? ''),
    timestamp: new Date().toISOString(),
  };
  console.warn('[compras][rpc-flag] comentarItens RPC desabilitado em tempo de execução', comentarItensRpcDisabled);
}

const isCriarRequisicaoRpcEnabled = (source?: string) => {
  if (runtimeRpcDisabled) {
    const snapshot: RpcFlagSnapshot = {
      enabled: false,
      override: null,
      envFlag: 'runtime-disabled',
      source,
      timestamp: new Date().toISOString(),
      runtimeDisabled: runtimeRpcDisabled,
    };
    publishRpcDebug(snapshot);
    return false;
  }

  const overrideRaw = (globalThis as any).__USE_CRIAR_REQUISICAO_RPC__;
  const override = typeof overrideRaw === 'boolean' ? overrideRaw : null;
  const flagRaw = (import.meta.env?.['VITE_COMPRAS_USE_CRIAR_REQUISICAO_RPC'] as string | undefined) ?? '0';
  const normalizedFlag = flagRaw.toString().trim().toLowerCase();
  const envEnabled = normalizedFlag === '1' || normalizedFlag === 'true';
  const enabled = typeof override === 'boolean' ? override : envEnabled;

  publishRpcDebug({
    enabled,
    override,
    envFlag: flagRaw,
    source,
    timestamp: new Date().toISOString(),
    runtimeDisabled: runtimeRpcDisabled,
  });

  return enabled;
};

const getRpcFlagSnapshot = () => lastRpcFlagSnapshot;

export type NovaReqInput = {
  obra_id: string;
  tipo_solicitacao?: 'orcamento' | 'pedido';
  urgencia?: 'rotina' | 'urgente' | 'emergencia';
  finalidade_id: string;                  // ✅ obrigatório
  prazo_entrega?: string | null;          // 'YYYY-MM-DD' ou ISO -> normalizamos
  retirada?: boolean;
  retirada_responsavel?: string | null;
  empresa_id?: string | null;             // opcional
  fornecedores?: string[];                // opcional
};

export async function criarRequisicaoAvancada(input: NovaReqInput): Promise<string> {
  // ✅ Validação: finalidade é obrigatória
  if (!input.finalidade_id || !input.finalidade_id.trim()) {
    throw new Error('Finalidade é obrigatória para criar uma requisição');
  }

  const payload = {
    p_obra: input.obra_id,
    p_tipo: input.tipo_solicitacao ?? 'orcamento',
    p_urgencia: input.urgencia ?? 'rotina',
    p_finalidade: input.finalidade_id,
    p_prazo: toDateOnly(input.prazo_entrega) ?? null,
    p_retirada: !!input.retirada,
    p_retirada_responsavel: input.retirada_responsavel ?? null,
    p_empresa: input.empresa_id ?? null,
    p_fornecedores: input.fornecedores ?? null
  };

  // 1) tenta RPC transacional (opt-in via VITE_COMPRAS_USE_CRIAR_REQUISICAO_RPC=1)
  if (isCriarRequisicaoRpcEnabled('criarRequisicaoAvancada')) {
    try {
      const { data, error } = await supabase.rpc('criar_requisicao', payload);
      if (!error && data) return data as string;

      if (error) {
        const is404 = (error as any)?.code === 'PGRST202' || (error as any)?.status === 404 || error.message?.includes('404');
        if (is404) {
          disableRpcAtRuntime('rpc_not_found', 'criarRequisicaoAvancada', error);
          console.warn('[comprasService] RPC criar_requisicao não encontrada (404), usando fallback de múltiplas queries.');
        } else if (error.code === '42501' || error.message?.includes('permission denied')) {
          disableRpcAtRuntime('permission_denied', 'criarRequisicaoAvancada', error);
          console.warn('[comprasService] RPC criar_requisicao sem permissão, fallback automático.', error);
        } else {
          disableRpcAtRuntime('rpc_error', 'criarRequisicaoAvancada', error);
          console.warn('[comprasService] RPC criar_requisicao falhou, usando fallback de múltiplas queries.', error);
        }
      }
    } catch (err) {
      disableRpcAtRuntime('rpc_exception', 'criarRequisicaoAvancada', err);
      console.warn('[comprasService] RPC criar_requisicao lançou exceção, fallback acionado.', err);
    }
  }
  // Obtém o usuário atual para criado_por (se disponível)
  let criadoPor: string | null = null;
  try {
    const { data: authData } = await supabase.auth.getUser();
    criadoPor = authData?.user?.id ?? null;
  } catch (authErr) {
    console.warn('⚠️ Não foi possível obter usuário atual para criado_por:', authErr);
  }

  const { data: ins, error: e1 } = await executeSupabaseQuery(
    getDbAcor()
    .from('compras_requisicoes')
    .insert({
      obra_id: payload.p_obra,
      status: 'solicitado',
      tipo_solicitacao: payload.p_tipo,
      urgencia: payload.p_urgencia,
      finalidade_id: payload.p_finalidade, // já validado acima
      prazo_entrega: payload.p_prazo,
      retirada: payload.p_retirada,
      retirada_responsavel: payload.p_retirada_responsavel,
      empresa_id: payload.p_empresa,
      criado_por: criadoPor
    })
    .select('id')
    .single()
  );
  if (e1) throw e1;
  const reqId = ins!.id as string;

  if (payload.p_fornecedores) {
    const rows = payload.p_fornecedores.map((fid: string) => ({ requisicao_id: reqId, fornecedor_id: fid }));
    const { error: e2 } = await executeSupabaseQuery(
      getDbAcor().from('compras_requisicoes_fornecedores').insert(rows)
    );
    if (e2) throw e2;
  }
  // Thread é garantida na RPC de comentário; não chamar ensureThreadForRequisicao aqui.
  // Comentário não é disparado neste momento; ele acontece apenas após o usuário salvar no editor.

  return reqId;
}

// -----------------------------------------
// Types
// -----------------------------------------
export type MetaRequisicao = {
  finalidade_id: string;                // ✅ obrigatório
  tipo_solicitacao?: 'orcamento' | 'pedido';
  urgencia?: 'rotina' | 'urgente' | 'emergencia';
  prazo_entrega?: string;              // 'YYYY-MM-DD'
  retirada?: boolean;
  retirada_responsavel?: string | null;
  numero_nota?: string | null;         // <- novo no meta quando salvar inline
  data_entregue?: string | null;       // 'YYYY-MM-DD'
  observacoes?: string;
  empresa_id?: string | null;
};

export type RequisicaoDetalhe = {
  id: string;
  obra_id: string;
  status: string;
  criado_em: string;
  updated_at: string | null;
  finalidade_id: string | null;
  tipo_solicitacao: 'orcamento' | 'pedido';
  urgencia: 'rotina' | 'urgente' | 'emergencia';
  prazo_entrega: string | null;        // date (yyyy-mm-dd) no banco
  retirada: boolean;
  retirada_responsavel: string | null;
  data_entregue: string | null;        // date
  numero_nota: string | null;
  empresa_id: string | null;
};

export type LogRequisicao = {
  id: string;
  requisicao_id: string;
  acao: string;
  campo: string | null;
  old: any | null;
  new: any | null;
  meta: any | null;
  changed_by: string | null;
  changed_at: string;
};

// ======================== FUNÇÕES SIMPLES ========================

export async function listarFinalidades() {
  const { data, error } = await executeSupabaseQuery(
    getDbAcor()
    .from('compras_finalidades')
    .select('id, nome')
    .order('nome', { ascending: true })
  );
  if (error) throw error;
  return data ?? [];
}

// Busca finalidades com filtro por nome
export async function buscarFinalidades(query: string) {
  let builder = getDbAcor()
    .from('compras_finalidades')
    .select('id, nome')
    .order('nome', { ascending: true });

  if (query?.trim()) {
    builder = builder.ilike('nome', `%${query.trim()}%`);
  }

  const { data, error } = await executeSupabaseQuery(builder);
  if (error) throw error;
  return data ?? [];
}

// NOVO: atualizar campos da requisição (meta)
export async function updateRequisicaoCampos(
  id: string,
  patch: Partial<MetaRequisicao>
) {
  const body: any = { ...patch };
  if (Object.prototype.hasOwnProperty.call(body, 'prazo_entrega')) {
    body.prazo_entrega = toDateOnly(body.prazo_entrega) ?? null; // garante date-only
  }
  if (Object.prototype.hasOwnProperty.call(body, 'data_entregue')) {
    body.data_entregue = toDateOnly(body.data_entregue) ?? null;
  }
  const { error } = await executeSupabaseQuery(
    getDbAcor()
    .from('compras_requisicoes')
    .update(body)
    .eq('id', id)
  );
  if (error) throw error;
}

// ========= Anexos (Storage) =========
export type RequisicaoArquivo = {
  path: string;
  name: string;
  mime?: string;
  size?: number;
  created_at?: string;
  url: string;
};

const BUCKET_REQ = 'compras-requisicoes'; // crie este bucket no Supabase Storage

export async function uploadArquivoRequisicao(reqId: string, file: File): Promise<RequisicaoArquivo> {
  const safeName = file.name.replace(/[^\w\-.]+/g, '_');
  const path = `${reqId}/anexos/${Date.now()}_${safeName}`;
  const { error } = await supabaseAcor.storage
    .from(BUCKET_REQ)
    .upload(path, file, { upsert: false, cacheControl: '31536000', contentType: file.type || undefined });
  if (error) throw error;
  const { data } = supabaseAcor.storage.from(BUCKET_REQ).getPublicUrl(path);
  return { path, name: file.name, mime: file.type, url: data.publicUrl };
}

export async function listarArquivosRequisicao(reqId: string): Promise<RequisicaoArquivo[]> {
  const dir = `${reqId}/anexos`;
  const { data, error } = await supabaseAcor.storage.from(BUCKET_REQ).list(dir, {
    limit: 200,
    sortBy: { column: 'created_at', order: 'asc' },
  });
  if (error && (error as any).message?.includes('not found')) return [];
  if (error) throw error;
  return (data ?? []).map((o: any) => {
    const fullPath = `${dir}/${o.name}`;
    const { data } = supabaseAcor.storage.from(BUCKET_REQ).getPublicUrl(fullPath);
    return {
      path: fullPath,
      name: o.name,
      size: o?.metadata?.size,
      created_at: o?.created_at,
      url: data.publicUrl,
    };
  });
}

export async function deleteArquivoRequisicao(path: string): Promise<void> {
  const { error } = await supabaseAcor.storage.from(BUCKET_REQ).remove([path]);
  if (error) throw error;
}

// 1) Itens da requisição (apenas o necessário pro comparativo)
export async function listarItens(requisicaoId: string) {
  const { data, error } = await supabase
    .schema('acor')
    .from('compras_requisicao_itens')
    .select(`
      id,
      requisicao_id,
      material_id,
      nome_digitado,
      unidade,
      quantidade,
      confianca_match
    `)
    .eq('requisicao_id', requisicaoId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((r: any) => ({
    id: r.id,
    material_id: r.material_id ?? null,
    nome_digitado: r.nome_digitado ?? "",
    unidade: r.unidade ?? "",
    quantidade: r.quantidade == null ? 0 : Number(r.quantidade),
    confianca_match: r.confianca_match ?? null,
  }));
}

// 2) Fornecedores que têm histórico para ao menos UM dos materiais
export async function buscarFornecedoresComHistoricoSimples(
  materialIds: string[],
  dias = 365
) {
  if (!materialIds.length) return [];
  const cutoff = format(subDays(new Date(), dias), 'yyyy-MM-dd');

  // pega pares (material_id, fornecedor_id) recentes
  const { data, error } = await supabase
    .from('mv_hist_precos')
    .select('material_id, fornecedor_canonico_id')
    .in('material_id', materialIds)
    .gte('data', cutoff)
    .not('fornecedor_canonico_id', 'is', null);

  if (error) {
    console.error('❌ Erro ao buscar fornecedores (simples):', error);
    throw error;
  }

  // conta por fornecedor
  const countByForn = new Map<string, number>();
  for (const r of data ?? []) {
    const id = r.fornecedor_canonico_id as string;
    countByForn.set(id, (countByForn.get(id) || 0) + 1);
  }

  const fornIds = Array.from(countByForn.keys());
  if (!fornIds.length) return [];

  // nomes dos fornecedores
  const { data: nomes, error: e2 } = await supabase
    .from('fornecedores_canonicos')
    .select('id, nome_canonico')
    .in('id', fornIds);

  if (e2) {
    console.error('❌ Erro ao buscar nomes de fornecedores:', e2);
    throw e2;
  }

  const fornecedores = (nomes ?? []).map((n: any) => ({
    fornecedor_id: n.id as string,
    nome: n.nome_canonico as string,
    linhas: countByForn.get(n.id) || 0,
  }));

  // ordena por relevância (mais linhas primeiro)
  fornecedores.sort((a: any, b: any) => b.linhas - a.linhas);
  return fornecedores;
}

// 3) Último preço por (material, fornecedor) no período
export async function listarHistoricoSimples(
  materialIds: string[],
  fornecedorIds: string[],
  dias = 365
) {
  if (!materialIds.length) return {};
  const cutoff = format(subDays(new Date(), dias), 'yyyy-MM-dd');

  let q = supabase
    .from('mv_hist_precos')
    .select('material_id, fornecedor_canonico_id, preco_unitario, data')
    .in('material_id', materialIds)
    .gte('data', cutoff)
    .order('data', { ascending: false });

  if (fornecedorIds?.length) {
    q = q.in('fornecedor_canonico_id', fornecedorIds);
  }

  const { data, error } = await q;
  if (error) {
    console.error('❌ Erro ao listar histórico (simples):', error);
    throw error;
  }

  // mantém a linha MAIS recente por par (material, fornecedor)
  const out: Record<string, Record<string, { preco: number; data: string }>> = {};
  for (const r of data ?? []) {
    const m = r.material_id as string;
    const f = r.fornecedor_canonico_id as string;
    (out[m] ||= {});
    if (!out[m][f]) {
      out[m][f] = { preco: Number(r.preco_unitario), data: r.data as string };
    }
  }
  return out;
}

// ======================== DEBUG HELPERS ========================
(function attachDebug() {
  const api = {
    peekRequisicaoItens: listarItens,
    peekHistorico: async (materialId: string, dias = 365) => {
      return listarHistoricoSimples([materialId], [], dias);
    },
    dumpComparativo: async (requisicaoId: string, dias = 365) => {
      const itens = await listarItens(requisicaoId);
      const mids = itens.map((i: any) => i.material_id).filter(Boolean) as string[];
      const fornecedores = await buscarFornecedoresComHistoricoSimples(mids, dias);
      const ofertas = await listarHistoricoSimples(
        mids,
        fornecedores.map((f: any) => f.fornecedor_id),
        dias
      );
      return { fornecedores, itens, ofertas };
    },
  };
  (window as any).comprasDebug = { ...(window as any).comprasDebug, ...api };
  console.log('🛠️ Debug tools disponíveis em window.comprasDebug');
})();

// Removido client duplicado (causava "Multiple GoTrueClient instances")
// window.comprasDebug = { supabase } // se quiser expor o mesmo singleton para debug

// =====================================================
// COMPRAS SERVICE - SIMPLIFICADO E FUNCIONAL
// Foca nas funcionalidades essenciais que sabemos que funcionam
// =====================================================

// 1) Tipos canônicos para a UI
export type SugestaoCotacao = {
  fornecedor_id: string;
  fornecedor_nome: string;
  itens_cobertos: number;
  itens_totais: number;
  alias_evidencias: string[]; // nomes encontrados/aliases usados para "provar" o match
  valor_itens_est: number;    // soma dos itens (estimado ou do histórico)
  valor_frete_est: number;    // estimativa de frete (0 se desconhecido)
  frete_incluso: boolean;     // true = frete incluso/grátis
  valor_total: number;        // itens + (frete se NÃO incluso)
};

// --- Tipos auxiliares ---
export type HistoricoPreco = {
  material_id: string;
  fornecedor_canonico_id: string;
  preco_unitario: number;
  data: string; // ISO
};

function normalizaSugestaoRow(r: any): SugestaoCotacao {
  const itens = Number(r.valor_itens_est ?? r.valor_itens ?? 0);
  const freteEst = Number(r.valor_frete_est ?? r.valor_frete ?? 0);
  const freteIncluso = Boolean(r.frete_incluso ?? r.frete_gratis ?? false);
  return {
    fornecedor_id: r.fornecedor_canonico_id ?? r.fornecedor_id,
    fornecedor_nome: r.nome_fornecedor ?? r.fornecedor_nome,
    itens_cobertos: Number(r.itens_cobertos ?? 0),
    itens_totais: Number(r.itens_totais ?? r.total_itens ?? 0),
    alias_evidencias: Array.isArray(r.alias_evidencias) ? r.alias_evidencias : (r.alias_evidencias ? [String(r.alias_evidencias)] : []),
    valor_itens_est: itens,
    valor_frete_est: freteEst,
    frete_incluso: freteIncluso,
    valor_total: itens + (freteIncluso ? 0 : freteEst),
  };
}

interface Material {
  id: string;
  nome: string;
  unidade?: string;
  unidade_padrao?: string;
  categoria?: string;
}

// =====================================================
// FUNÇÕES PRINCIPAIS - TESTADAS E FUNCIONAIS
// =====================================================

// --- Fornecedores (lista para cotação) ---
export type FornecedorCotacao = {
  id: string;
  nome: string;
  categoria_fornecedor: string | null;
  cidade: string | null;
  filiais_count: number | null;
  matriz: string | null;
  filiais: string | null;
};

export async function listarFornecedoresCotacao(q: string, limit = 200): Promise<FornecedorCotacao[]> {
  const base = supabase.from('vw_fornecedores_cotacao').select('*').order('nome', { ascending: true }).limit(limit);
  const { data, error } = q?.trim()
    ? await base.ilike('nome', `%${q.trim()}%`)
    : await base;
  if (error) throw error;
  return (data ?? []) as any[];
}

// (removido) relacionamento N:N de fornecedores — agora é empresa única via empresa_id

// --- Painel de Compras com datas/novos campos (usa a view vw_compras_painel) ---
export type PainelRequisicao = {
  requisicao_id: string;
  obra_id: string;
  obra_nome: string | null;
  status_requisicao: string;
  criado_em: string;
  prazo_entrega: string | null;
  data_entregue: string | null;
  numero_nota: string | null;
  tipo_solicitacao: 'orcamento' | 'pedido';
  urgencia: 'rotina' | 'urgente' | 'emergencia';
  retirada: boolean;
  retirada_responsavel: string | null;
  observacoes: string | null;
  finalidade_nome: string | null;
  itens_qtd: number;
  empresa_id: string | null;
  empresa_nome: string | null;
  /** novos campos da view: prévia com até 5 itens */
  itens_preview?: string[];
  itens_preview_text?: string | null;
};

export async function listarPainelCompras(): Promise<PainelRequisicao[]> {
  const { data, error } = await executeSupabaseQuery(
    getDbAcor()
    .from('vw_compras_painel')
    .select(`
      requisicao_id, obra_id, obra_nome, status_requisicao,
      criado_em, prazo_entrega, data_entregue, numero_nota,
      tipo_solicitacao, urgencia, retirada, retirada_responsavel,
      observacoes, finalidade_nome, itens_qtd,
      empresa_id, empresa_nome,
      itens_preview, itens_preview_text
    `)
    .order('criado_em', { ascending: false })
  );
  if (error) throw error;
  
  return (data ?? []).map((r: any) => ({
    ...r,
    empresa_id: r.empresa_id ?? null,
    empresa_nome: r.empresa_nome ?? null,
    itens_preview: r.itens_preview ?? [],
    itens_preview_text: r.itens_preview_text ?? null,
  }));
}

export async function buscarMateriais(q: string) {
  try {
    console.log('🔍 Buscando materiais:', q);

    const normalizeMaterial = (item: any): Material => {
      const unidade = item?.unidade ?? item?.unidade_padrao ?? 'un';
      const normalized: Material = {
        id: item?.id,
        nome: item?.nome,
        unidade,
      };
      if (item?.unidade_padrao) normalized.unidade_padrao = item.unidade_padrao;
      if (item?.categoria) normalized.categoria = item.categoria;
      return normalized;
    };
    
    // Primeiro tentar função RPC
    const { data, error } = await supabase.rpc('fn_materiais_sugerir', { 
      p_q: q, 
      p_limit: 8 
    });

    if (!error && data) {
      // Converter JSON para array se necessário
      let materiais = data;
      if (typeof data === 'string') {
        try {
          materiais = JSON.parse(data);
        } catch {
          console.warn('⚠️ Erro ao parsear JSON, usando dados diretos');
          return [];
        }
      }

      if (!Array.isArray(materiais)) {
        console.warn('⚠️ Dados não são array:', typeof materiais);
        return [];
      }

      // Mapear para formato esperado pelo frontend
      const resultado: Material[] = materiais.map(normalizeMaterial);

      console.log(`✅ ${resultado.length} materiais encontrados via RPC`);
      return resultado;
    }
    
    // Fallback: tentar view vw_materiais_busca primeiro (nova view específica para busca)
    try {
      const { data: materiaisBusca, error: erroBusca } = await supabaseAcor
        .from('vw_materiais_busca')
        .select('id, nome, unidade_padrao, categoria, status_uso')
        .ilike('nome', `%${q}%`)
        .limit(8);
      
      if (!erroBusca && materiaisBusca && materiaisBusca.length > 0) {
        const resultado: Material[] = materiaisBusca.map(normalizeMaterial);

        console.log(`✅ ${resultado.length} materiais encontrados via vw_materiais_busca`);
        return resultado;
      }
      
    } catch (err) {
      // Silenciar erro - view pode não existir
    }
    
    // Fallback: tentar view vw_materiais_completos
    try {
      const { data: materiaisCompletos, error: erroCompletos } = await supabaseAcor
        .from('vw_materiais_completos')
        .select('id, nome, unidade_padrao, categoria')
        .ilike('nome', `%${q}%`)
        .eq('ativo', true)
        .limit(8);
      
      if (!erroCompletos && materiaisCompletos && materiaisCompletos.length > 0) {
        const resultado: Material[] = materiaisCompletos.map(normalizeMaterial);

        console.log(`✅ ${resultado.length} materiais encontrados via vw_materiais_completos`);
        return resultado;
      }
      
    } catch (err) {
      // Silenciar erro - view pode não existir
    }
    
    // Fallback: tentar outras tabelas (silenciosamente)
    const tabelasParaTestar = [
      'materiais_canonicos',
      'catalogo_materiais',
      'produtos'
    ];
    
    for (const tabela of tabelasParaTestar) {
      try {
        const { data: materiaisDiretos, error: erroDireto } = await supabaseAcor
          .from(tabela)
          .select('id, nome, unidade_padrao, categoria')
          .ilike('nome', `%${q}%`)
          .limit(8);
        
        if (!erroDireto && materiaisDiretos && materiaisDiretos.length > 0) {
          const resultado: Material[] = materiaisDiretos.map(normalizeMaterial);

          console.log(`✅ ${resultado.length} materiais encontrados via ${tabela}`);
          return resultado;
        }
        
      } catch (err) {
        // Silenciar erro - tabela pode não existir
      }
    }
    
    console.log('ℹ️ Nenhum material encontrado para:', q);
    return [];

  } catch (err) {
    console.error('❌ Erro geral na busca de materiais:', err);
    return [];
  }
}

// --- NOVO: últimos preços por material (1 chamada para N materiais) ---
export async function listarUltimosPrecosHistorico(materialIds: string[]): Promise<HistoricoPreco[]> {
  const ids = (materialIds || []).filter(Boolean);
  if (!ids.length) return [];

  const { data, error } = await supabaseAcor
    .from('vw_stg_to_historico_mapeado')
    .select('material_id, fornecedor_canonico_id, preco_unitario, data')
    .in('material_id', ids)
    .not('fornecedor_canonico_id', 'is', null)
    .order('material_id', { ascending: true })
    .order('fornecedor_canonico_id', { ascending: true })
    .order('data', { ascending: false });

  if (error) throw error;

  // Mantém só o registro MAIS RECENTE por (material_id, fornecedor)
  const keep = new Map<string, HistoricoPreco>();
  for (const r of (data || []) as any[]) {
    const key = `${r.material_id}::${r.fornecedor_canonico_id}`;
    if (!keep.has(key)) keep.set(key, r); // o primeiro já é o mais novo por causa do ORDER acima
  }
  return [...keep.values()];
}



// aceita meta opcional
export async function criarRequisicaoVazia(obraId: string, meta?: MetaRequisicao): Promise<string | null> {
  // ✅ Validação: finalidade é obrigatória
  if (!meta?.finalidade_id || !meta.finalidade_id.trim()) {
    throw new Error('Finalidade é obrigatória para criar uma requisição');
  }

  const tentarRpc = isCriarRequisicaoRpcEnabled('criarRequisicaoVazia');
  const metaKeys = Object.keys(meta || {});
  console.info('[compras] criarRequisicaoVazia:start', {
    obraId,
    metaKeys,
    tentarRpc,
    rpcFlag: getRpcFlagSnapshot(),
  });

  if (tentarRpc) {
    try {
      const { data, error } = await supabase.rpc('fn_requisicao_criar', { p_obra_id: obraId });
      if (!error && data) {
        const reqId = data as string;
        if (reqId && meta && Object.keys(meta).length) {
          await supabase.from('compras_requisicoes').update(meta).eq('id', reqId);
        }
        console.info('[compras] criarRequisicaoVazia:rpc-ok', { obraId, reqId });
        return reqId;
      }

      if (error && (error.code === '42501' || error.message?.includes('permission denied'))) {
        disableRpcAtRuntime('permission_denied', 'criarRequisicaoVazia', error);
        console.warn('⚠️ criarRequisicaoVazia: RPC falhou por permissão, usando fallback direto na tabela.', error);
      } else if (error) {
        disableRpcAtRuntime('rpc_error', 'criarRequisicaoVazia', error);
        console.error('❌ criarRequisicaoVazia erro:', error);
        return null;
      }
    } catch (err: any) {
      disableRpcAtRuntime('rpc_exception', 'criarRequisicaoVazia', err);
      console.error('❌ Erro ao criar requisição via RPC:', err);
      // cai para o fallback
    }
  }

  try {
    console.info('[compras] criarRequisicaoVazia:fallback', {
      obraId,
      metaKeys,
      motivo: tentarRpc ? 'rpc_falhou' : 'rpc_desligado',
    });
    let criadoPor: string | null = null;
    try {
      const { data: authData } = await supabase.auth.getUser();
      criadoPor = authData?.user?.id ?? null;
    } catch (authErr) {
      console.warn('⚠️ Não foi possível obter usuário atual:', authErr);
    }

    const { data: ins, error: insertError } = await executeSupabaseQuery(
      getDbAcor()
        .from('compras_requisicoes')
        .insert({
          obra_id: obraId,
          status: 'solicitado',
          tipo_solicitacao: meta?.tipo_solicitacao ?? 'orcamento',
          urgencia: meta?.urgencia ?? 'rotina',
          finalidade_id: meta.finalidade_id, // já validado acima
          prazo_entrega: meta?.prazo_entrega ?? null,
          retirada: meta?.retirada ?? false,
          retirada_responsavel: meta?.retirada_responsavel ?? null,
          criado_por: criadoPor,
          ...(meta?.observacoes ? { observacoes: meta.observacoes } : {})
        })
        .select('id')
        .single()
    );

    if (insertError) {
      console.error('❌ criarRequisicaoVazia fallback erro:', insertError);
      return null;
    }

    const novoId = ins!.id as string;
    console.info('[compras] criarRequisicaoVazia:fallback-ok', { obraId, reqId: novoId });
    return novoId;
  } catch (err) {
    console.error('❌ Erro ao criar requisição (fallback):', err);
    return null;
  }
}

// aceita meta opcional, mas finalidade é obrigatória
export async function criarRequisicaoDeTexto(obraId: string, textoColado: string, meta?: MetaRequisicao): Promise<string | null> {
  // ✅ Validação: finalidade é obrigatória
  if (!meta?.finalidade_id || !meta.finalidade_id.trim()) {
    throw new Error('Finalidade é obrigatória para criar uma requisição');
  }

  const reqId = await criarRequisicaoVazia(obraId, meta);
  if (!reqId) return null;

  try {
    const { error } = await supabase.rpc('fn_requisicao_parse_text', { 
      p_requisicao_id: reqId, 
      p_texto: textoColado, 
      p_score_min: 0.25 
    });
    if (error) { 
      console.warn('⚠️ Parse texto erro:', error);
      return reqId;
    }
    return reqId;
  } catch (err) {
    console.warn('⚠️ Erro ao processar texto:', err);
    return reqId;
  }
}


export async function addItem(requisicaoId: string) {
  try {
    // Primeiro tentar função RPC
    const { data, error } = await supabase.rpc('fn_item_add', { p_requisicao_id: requisicaoId });
    
    if (!error && data) {
      return data as string;
    }
    
    // Fallback: inserir diretamente na tabela
    const { data: novoItem, error: erroInsercao } = await supabaseAcor
      .from('compras_requisicao_itens')
      .insert({
        requisicao_id: requisicaoId,
        nome_digitado: '',
        unidade: 'un',
        quantidade: 0,
        material_id: null
      })
      .select('id')
      .single();
    
    if (erroInsercao) {
      console.error('❌ Erro na inserção direta:', erroInsercao);
      return null;
    }
    
    return novoItem.id;
    
  } catch (err) {
    console.error('❌ Erro ao adicionar item:', err);
    return null;
  }
}

export async function updateItemCampos(
  itemId: string,
  patch: Partial<{ nome_digitado: string; unidade: string; quantidade: number; material_id: string | null; }>
) {
  const payload: any = {};
  if ('nome_digitado' in patch) payload.nome_digitado = patch.nome_digitado;
  if ('unidade' in patch) payload.unidade = (patch.unidade ?? '').trim();
  if ('quantidade' in patch) payload.quantidade = patch.quantidade == null ? null : Number(patch.quantidade);
  if ('material_id' in patch) payload.material_id = patch.material_id;

  const { error } = await supabase
    .schema('acor')
    .from('compras_requisicao_itens')
    .update(payload)
    .eq('id', itemId);

  if (error) throw error;
  return true;
}

export async function delItem(itemId: string) {
  try {
    // Primeiro tentar função RPC
    const { error } = await supabase.rpc('fn_item_delete', { p_item_id: itemId });
    
    if (!error) {
      return true;
    }
    
    // Fallback: delete direto na tabela
    const { error: erroDelete } = await supabaseAcor
      .from('compras_requisicao_itens')
      .delete()
      .eq('id', itemId);
    
    if (erroDelete) {
      console.error('❌ Erro no delete direto:', erroDelete);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('❌ Erro ao deletar item:', err);
    return false;
  }
}

export async function updateStatusRequisicao(
  id: string,
  novoStatus: string,
  statusAnterior?: string | null
) {
  let previousStatus = statusAnterior;

  if (previousStatus === undefined) {
    try {
      const { data } = await executeSupabaseQuery(
        getDbAcor()
          .from('compras_requisicoes')
          .select('status')
          .eq('id', id)
          .single()
      );
      previousStatus = (data as { status?: string | null } | null)?.status ?? null;
    } catch (fetchError) {
      console.warn('⚠️ updateStatusRequisicao: falha ao buscar status atual', fetchError);
      previousStatus = null;
    }
  }

  const { error } = await executeSupabaseQuery(
    getDbAcor()
      .from('compras_requisicoes')
      .update({ status: novoStatus })
      .eq('id', id)
  );
  if (error) throw error;

  if ((previousStatus ?? null) === (novoStatus ?? null)) {
    return;
  }

  try {
    const { data: authData } = await supabase.auth.getUser();
    const usuarioId = authData?.user?.id ?? null;

    const { error: rpcError } = await supabase.rpc('fn_requisicao_status_comment', {
      p_requisicao_id: id,
      p_status_anterior: previousStatus,
      p_status_novo: novoStatus,
      p_usuario_id: usuarioId,
    });

    if (rpcError) {
      console.error('⚠️ updateStatusRequisicao: falha ao registrar comentário automático de status', rpcError);
    }
  } catch (rpcError) {
    console.error('⚠️ updateStatusRequisicao: erro ao preparar comentário automático de status', rpcError);
  }
}

export async function buscarDetalhesRequisicao(id: string): Promise<RequisicaoDetalhe> {
  const { data, error } = await executeSupabaseQuery(
    getDbAcor()
    .from('compras_requisicoes')
    .select(`
      id, obra_id, status, criado_em, updated_at,
      finalidade_id, tipo_solicitacao, urgencia,
      prazo_entrega, retirada, retirada_responsavel,
      data_entregue, numero_nota, empresa_id
    `)
    .eq('id', id)
    .single()
  );
  if (error) throw error;
  // retorna sem mutar datas; a UI já normaliza quando necessário
  return data as RequisicaoDetalhe;
}

// =====================================================
// FUNÇÕES DE COTAÇÃO E PEDIDOS
// =====================================================

// Função para calcular valor total estimado baseado no histórico de preços
async function calcularValorTotalEstimado(materialIds: string[], fornecedorId: string) {
  try {
    if (!materialIds || materialIds.length === 0) {
      return 0;
    }
    
    console.log(`💰 Calculando valor total para fornecedor ${fornecedorId.slice(0,8)} e ${materialIds.length} materiais`);
    console.log(`📋 Materiais: ${materialIds.map(id => id.slice(0,8)).join(', ')}`);
    
    // Buscar histórico de preços para os materiais e fornecedor
    const { data: historico, error } = await supabaseAcor
      .from('historico_precos')
      .select(`
        material_id,
        preco_unitario,
        quantidade_comprada,
        preco_total,
        data_compra
      `)
      .in('material_id', materialIds)
      .eq('fornecedor_canonico_id', fornecedorId)
      .not('preco_unitario', 'is', null)
      .order('data_compra', { ascending: false });
    
    if (error) {
      console.warn('⚠️ Erro ao buscar histórico para cálculo:', error.message);
      return 0;
    }
    
    if (!historico || historico.length === 0) {
      console.log('ℹ️ Nenhum histórico encontrado para cálculo de valor');
      
      // Vamos verificar se existe algum histórico para este fornecedor
      const { data: historicoFornecedor, error: erroFornecedor } = await supabaseAcor
        .from('historico_precos')
        .select('material_id, preco_unitario')
        .eq('fornecedor_canonico_id', fornecedorId)
        .limit(3);
      
      if (!erroFornecedor && historicoFornecedor && historicoFornecedor.length > 0) {
        console.log(`📊 Fornecedor ${fornecedorId.slice(0,8)} tem ${historicoFornecedor.length} registros de histórico geral`);
        console.log('🔍 Exemplos de materiais deste fornecedor:', historicoFornecedor.map(h => h.material_id.slice(0,8)));
      }
      
      return 0;
    }
    
    console.log(`📊 ${historico.length} registros de histórico encontrados para cálculo`);
    
    // Agrupar por material e pegar o preço mais recente
    const precosPorMaterial = new Map();
    
    historico.forEach((item: any) => {
      if (!precosPorMaterial.has(item.material_id)) {
        precosPorMaterial.set(item.material_id, {
          preco_unitario: parseFloat(item.preco_unitario) || 0,
          quantidade_comprada: parseFloat(item.quantidade_comprada) || 1,
          preco_total: parseFloat(item.preco_total) || 0,
          data_compra: item.data_compra
        });
      }
    });
    
    // Calcular valor total estimado
    let valorTotal = 0;
    let materiaisComPreco = 0;
    
    for (const [materialId, dados] of precosPorMaterial) {
      if (dados.preco_unitario > 0) {
        // Usar preço total se disponível, senão calcular
        if (dados.preco_total > 0) {
          valorTotal += dados.preco_total;
        } else {
          valorTotal += dados.preco_unitario * dados.quantidade_comprada;
        }
        materiaisComPreco++;
        console.log(`📦 Material ${materialId.slice(0,8)}: R$ ${dados.preco_unitario} x ${dados.quantidade_comprada} = R$ ${dados.preco_total || (dados.preco_unitario * dados.quantidade_comprada)}`);
      }
    }
    
    // Se não temos preços para todos os materiais, estimar baseado na média
    if (materiaisComPreco > 0 && materiaisComPreco < materialIds.length) {
      const precoMedio = valorTotal / materiaisComPreco;
      const materiaisSemPreco = materialIds.length - materiaisComPreco;
      const valorEstimado = precoMedio * materiaisSemPreco;
      valorTotal += valorEstimado;
      console.log(`📊 Estimativa para ${materiaisSemPreco} materiais sem preço: R$ ${valorEstimado.toFixed(2)}`);
    }
    
    console.log(`💰 Valor total estimado para fornecedor ${fornecedorId.slice(0,8)}: R$ ${valorTotal.toFixed(2)}`);
    return valorTotal;
    
  } catch (err) {
    console.warn('⚠️ Erro ao calcular valor total:', err);
    return 0;
  }
}

// 2) Garante o shape e a ordenação (cobertura desc, preço asc)
export async function sugerirCotacoes(requisicaoId: string): Promise<SugestaoCotacao[]> {
  try {
    console.log('🔍 Sugerindo cotações para requisição:', requisicaoId.slice(0,8));
    
    // Tentar função de cotação simples primeiro (se existir)
    const itens = await listarItens(requisicaoId);
    console.log(`📋 ${itens.length} itens encontrados na requisição`);
    
    const materialIds = itens
      .filter((item: any) => item.material_id)
      .map((item: any) => item.material_id);
    
    console.log(`🔗 ${materialIds.length} materiais vinculados encontrados`);

    if (materialIds.length > 0) {
      console.log('🔄 Tentando função fn_cotacao_simples...');
      
      try {
        const { data, error } = await supabase.rpc('fn_cotacao_simples', { 
          p_material_ids: materialIds 
        });
        
        if (!error && data && Array.isArray(data)) {
          console.log(`✅ ${data.length} cotações encontradas via RPC`);
          // ... (mantém sua chamada à RPC ou fallback)
          // supondo que 'data' é o array vindo da RPC/fallback:
          const plano = data.map(normalizaSugestaoRow)
            .sort((a, b) =>
              b.itens_cobertos - a.itens_cobertos || a.valor_total - b.valor_total
            );
          return plano;
        }
        
        if (error) {
          console.warn('⚠️ Função fn_cotacao_simples não disponível:', error.message);
        }
      } catch (err) {
        console.warn('⚠️ Função fn_cotacao_simples não existe');
      }
    }
    
    // Fallback: buscar fornecedores com histórico de preços
    console.log('🔄 Buscando fornecedores com histórico de preços...');
    
    try {
      // Buscar fornecedores que têm histórico de preços para os materiais
      let fornecedoresComHistorico: any[] = [];
      
      if (materialIds.length > 0) {
        fornecedoresComHistorico = await buscarFornecedoresComHistorico(materialIds);
      }
      
      // Se não há fornecedores com histórico, buscar todos os fornecedores ativos
      if (fornecedoresComHistorico.length === 0) {
        console.log('🔄 Buscando fornecedores ativos...');
        
        const { data: fornecedoresAtivos, error: erroFornecedores } = await supabaseAcor
          .from('fornecedores_canonicos')
          .select('id, nome_canonico, categoria_fornecedor, cidade')
          .eq('ativo', true)
          .limit(5);
        
        if (!erroFornecedores && fornecedoresAtivos && fornecedoresAtivos.length > 0) {
          console.log(`✅ ${fornecedoresAtivos.length} fornecedores ativos encontrados`);
          fornecedoresComHistorico = fornecedoresAtivos.map((f: any) => ({
            id: f.id,
            nome: f.nome_canonico,
            categoria: f.categoria_fornecedor,
            cidade: f.cidade,
            materiais_historico: [],
            total_compras: 0
          }));
        }
      }
      
      if (fornecedoresComHistorico.length > 0) {
        // Calcular valores totais para cada fornecedor
        const cotacoesComValores = await Promise.all(
          fornecedoresComHistorico.map(async (fornecedor: any) => {
            let valorTotal = 0;
            let itensAtendidos = 0;
            
            // Se o fornecedor tem histórico para os materiais específicos
            if (fornecedor.materiais_historico && fornecedor.materiais_historico.length > 0) {
              valorTotal = await calcularValorTotalEstimado(materialIds, fornecedor.id);
              itensAtendidos = Math.min(fornecedor.materiais_historico.length, materialIds.length);
              console.log(`💰 Fornecedor ${fornecedor.nome}: R$ ${valorTotal.toFixed(2)} (${itensAtendidos}/${materialIds.length} materiais com histórico)`);
            } else {
              // Estimar valor baseado em preços médios do mercado
              valorTotal = await estimarValorSemHistorico(materialIds, fornecedor.id);
              itensAtendidos = Math.min(1, materialIds.length || 1);
              console.log(`💰 Fornecedor ${fornecedor.nome}: R$ ${valorTotal.toFixed(2)} (estimativa sem histórico)`);
            }
            
            return normalizaSugestaoRow({
              fornecedor_id: fornecedor.id,
              fornecedor_nome: fornecedor.nome,
              frete_incluso: false,
              itens_cobertos: itensAtendidos,
              itens_totais: materialIds.length || 1,
              valor_itens_est: valorTotal,
              valor_frete_est: 0,
              valor_total: valorTotal,
              alias_evidencias: []
            });
          })
        );
        
        // Ordenar por cobertura (desc) e preço (asc)
        cotacoesComValores.sort((a, b) =>
          b.itens_cobertos - a.itens_cobertos || a.valor_total - b.valor_total
        );
        
        console.log(`✅ ${cotacoesComValores.length} cotações geradas com valores`);
        return cotacoesComValores;
      }
      
    } catch (err) {
      console.warn('⚠️ Erro no fallback de fornecedores:', err);
    }
    
    // Último fallback: fornecedores mock para demonstração
    console.log('🔄 Usando fornecedores mock para demonstração...');
    const mockData = [
      {
        fornecedor_id: 'mock-1',
        fornecedor_nome: 'Fornecedor A',
        frete_incluso: false,
        itens_cobertos: materialIds.length > 0 ? Math.min(materialIds.length, 2) : 1,
        itens_totais: materialIds.length || 1,
        valor_itens_est: 1500.00,
        valor_frete_est: 0,
        valor_total: 1500.00,
        alias_evidencias: []
      },
      {
        fornecedor_id: 'mock-2',
        fornecedor_nome: 'Fornecedor B',
        frete_incluso: true,
        itens_cobertos: materialIds.length > 0 ? Math.min(materialIds.length, 1) : 1,
        itens_totais: materialIds.length || 1,
        valor_itens_est: 1800.00,
        valor_frete_est: 0,
        valor_total: 1800.00,
        alias_evidencias: []
      }
    ];
    
    return mockData.map(normalizaSugestaoRow)
      .sort((a, b) =>
        b.itens_cobertos - a.itens_cobertos || a.valor_total - b.valor_total
      );
  } catch (err) {
    console.error('❌ Erro ao sugerir cotações:', err);
    return [];
  }
}

// export async function gerarMensagemPedido(requisicaoId: string, fornecedorId: string) {
//   try {
//     console.log('🔍 Gerando mensagem de pedido para requisição:', requisicaoId.slice(0,8), 'fornecedor:', fornecedorId.slice(0,8));
//     
//     // Primeiro tentar função RPC
//     const { data, error } = await supabaseAcor.rpc('fn_pedido_whatsapp', { 
//       p_requisicao: requisicaoId, 
//       p_fornecedor: fornecedorId 
//     });
//     
//     if (!error && data) {
//       console.log('✅ Mensagem gerada via RPC');
//       return (data as string) ?? '';
//     }
//     
//     if (error) {
//       console.warn('⚠️ Função fn_pedido_whatsapp falhou:', error.message);
//     }
//     
//     // Fallback: gerar mensagem manualmente usando resumo do fornecedor
//     console.log('🔄 Gerando mensagem manualmente...');
//     
//     try {
//       // Buscar resumo do fornecedor para obter totais já calculados
//       const resumo = await listarResumoFornecedores(requisicaoId);
//       const alvo = resumo.find(r => r.fornecedor_id === fornecedorId);
//       const total = alvo?.valor_total ?? 0;
//       const prazoMax = alvo?.prazo_max ?? null;
//       
//       // Buscar detalhes da requisição
//       const detalhesRequisicao = await buscarDetalhesRequisicao(requisicaoId);
//       if (!detalhesRequisicao) {
//         console.warn('⚠️ Não foi possível buscar detalhes da requisição');
//         return gerarMensagemPadrao(requisicaoId, fornecedorId);
//       }
//       
//       // Buscar itens da requisição
//       const itens = await listarItens(requisicaoId);
//       if (!itens || itens.length === 0) {
//         console.warn('⚠️ Nenhum item encontrado na requisição');
//         return gerarMensagemPadrao(requisicaoId, fornecedorId);
//       }
//       
//       // Buscar informações do fornecedor
//       const fornecedor = await buscarDetalhesFornecedor(fornecedorId);
//       
//       // Gerar mensagem manual com dados do resumo
//       const mensagem = gerarMensagemManual(detalhesRequisicao, itens, fornecedor, total, prazoMax);
//       console.log('✅ Mensagem gerada manualmente');
//       return mensagem;
//       
//     } catch (err) {
//       console.warn('⚠️ Erro ao gerar mensagem manual:', err);
//       return gerarMensagemPadrao(requisicaoId, fornecedorId);
//     }
//     
//   } catch (err) {
//     console.error('❌ Erro ao gerar mensagem:', err);
//     return gerarMensagemPadrao(requisicaoId, fornecedorId);
//   }
// }
// 
// // Função auxiliar para buscar detalhes do fornecedor
// async function buscarDetalhesFornecedor(fornecedorId: string) {
//   try {
//     // Tentar buscar na tabela de fornecedores canônicos
//     const { data: fornecedor, error } = await supabaseAcor
//       .from('fornecedores_canonicos')
//       .select('id, nome_canonico, categoria_fornecedor, cidade')
//       .eq('id', fornecedorId)
//       .single();
//     
//     if (!error && fornecedor) {
//       return fornecedor;
//     }
//     
//     // Tentar buscar na tabela de aliases
//     const { data: alias, error: erroAlias } = await supabaseAcor
//       .from('fornecedores_aliases')
//       .select(`
//         id,
//         nome_original,
//         fornecedores_canonicos!inner(
//           id,
//           nome_canonico,
//           categoria_fornecedor,
//           cidade
//         )
//       `)
//       .eq('id', fornecedorId)
//       .single();
//     
//     if (!erroAlias && alias && alias.fornecedores_canonicos) {
//       const fornecedorCanonico = alias.fornecedores_canonicos as any;
//       return {
//         id: fornecedorCanonico.id,
//         nome_canonico: fornecedorCanonico.nome_canonico,
//         categoria_fornecedor: fornecedorCanonico.categoria_fornecedor,
//         cidade: fornecedorCanonico.cidade,
//         nome_original: alias.nome_original
//       };
//     }
//     
//     return null;
//   } catch (err) {
//     console.warn('⚠️ Erro ao buscar detalhes do fornecedor:', err);
//     return null;
//   }
// }
// 
// // Função para gerar mensagem manual
// function gerarMensagemManual(detalhesRequisicao: any, itens: any[], fornecedor: any, valorTotal?: number, prazoMax?: number) {
//   const dataAtual = new Date().toLocaleDateString('pt-BR');
//   const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
//   
//   let mensagem = `📋 *PEDIDO DE COMPRAS*\n\n`;
//   mensagem += `📅 Data: ${dataAtual} às ${horaAtual}\n`;
//   mensagem += `🏗️ Obra: ${detalhesRequisicao.obra_id || 'Não informada'}\n`;
//   
//   if (fornecedor) {
//     mensagem += `🏢 Fornecedor: ${fornecedor.nome_canonico || fornecedor.nome_original || 'Fornecedor'}\n`;
//     if (fornecedor.cidade) {
//       mensagem += `📍 Cidade: ${fornecedor.cidade}\n`;
//     }
//   }
//   
//   mensagem += `\n📦 *ITENS SOLICITADOS:*\n\n`;
//   
//   itens.forEach((item, index) => {
//     const quantidade = item.quantidade || 0;
//     const unidade = item.unidade || 'un';
//     const nome = item.nome_digitado || item.material_id || 'Item sem descrição';
//     
//     mensagem += `${index + 1}. ${nome}\n`;
//     mensagem += `   📊 Quantidade: ${quantidade} ${unidade}\n`;
//     
//     if (item.observacao) {
//       mensagem += `   📝 Observação: ${item.observacao}\n`;
//     }
//     mensagem += `\n`;
//   });
//   
//   mensagem += `\n💰 *INFORMAÇÕES ADICIONAIS:*\n`;
//   
//   // Usar valor total do resumo se disponível
//   if (valorTotal && valorTotal > 0) {
//     mensagem += `• Valor estimado total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
//   }
//   
//   // Usar prazo máximo do resumo se disponível
//   if (prazoMax && prazoMax > 0) {
//     mensagem += `• Prazo máximo desejado: ${prazoMax} dias\n`;
//   }
//   
//   mensagem += `• Por favor, informe disponibilidade e preços\n`;
//   mensagem += `• Prazo de entrega desejado\n`;
//   mensagem += `• Condições de pagamento\n`;
//   mensagem += `• Frete incluso ou não\n\n`;
//   
//   mensagem += `📞 Aguardamos seu retorno!\n`;
//   mensagem += `\n_*Mensagem gerada automaticamente pelo sistema ACOR*_`;
//   
//   return mensagem;
// }
// 
// // Função para gerar mensagem padrão quando não há dados
// function gerarMensagemPadrao(requisicaoId: string, fornecedorId: string) {
//   const dataAtual = new Date().toLocaleDateString('pt-BR');
//   const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
//   
//   return `📋 *PEDIDO DE COMPRAS*\n\n` +
//          `📅 Data: ${dataAtual} às ${horaAtual}\n` +
//          `🆔 Requisição: ${requisicaoId.slice(0,8)}\n` +
//          `🏢 Fornecedor: ${fornecedorId.slice(0,8)}\n\n` +
//          `📦 *ITENS SOLICITADOS:*\n` +
//          `• Verificar itens na requisição ${requisicaoId.slice(0,8)}\n\n` +
//          `💰 *INFORMAÇÕES ADICIONAIS:*\n` +
//          `• Por favor, informe disponibilidade e preços\n` +
//          `• Prazo de entrega desejado\n` +
//          `• Condições de pagamento\n` +
//          `• Frete incluso ou não\n\n` +
//          `📞 Aguardamos seu retorno!\n\n` +
//          `_*Mensagem gerada automaticamente pelo sistema ACOR*_`;
// }
// 
// export async function salvarPedido(requisicaoId: string, fornecedorId: string, mensagem: string) {
//   try {
//     console.log('💾 Salvando pedido para requisição:', requisicaoId.slice(0,8), 'fornecedor:', fornecedorId.slice(0,8));
//     
//     // Buscar detalhes da requisição para obter obra_id
//     const detalhesRequisicao = await buscarDetalhesRequisicao(requisicaoId);
//     const obraId = detalhesRequisicao?.obra_id || null;
//     
//     const dadosPedido = {
//       requisicao_id: requisicaoId,
//       fornecedor_id: fornecedorId,
//       mensagem_whatsapp: mensagem,
//       enviado_em: new Date().toISOString(),
//       obra_id: obraId,
//       status: 'Aguardando',
//       criado_em: new Date().toISOString()
//     };
//     
//     console.log('📋 Dados do pedido:', dadosPedido);
//     
//     const { data, error } = await supabaseAcor
//       .from('compras_pedidos')
//       .insert(dadosPedido)
//       .select('id')
//       .single();
//     
//     if (error) {
//       console.error('❌ salvarPedido erro:', error);
//       
//       // Fallback: tentar inserir sem campos opcionais
//       console.log('🔄 Tentando fallback: inserção simplificada...');
//       
//       const { error: erroFallback } = await supabaseAcor
//         .from('compras_pedidos')
//         .insert({
//           requisicao_id: requisicaoId,
//           fornecedor_id: fornecedorId,
//           mensagem_whatsapp: mensagem,
//           enviado_em: new Date().toISOString()
//         });
//       
//       if (erroFallback) {
//         console.error('❌ Erro no fallback também:', erroFallback);
//         return false;
//       }
//       
//       console.log('✅ Pedido salvo via fallback');
//       return true;
//     }
//     
//     console.log('✅ Pedido salvo com sucesso, ID:', data?.id);
//     return true;
//     
//   } catch (err) {
//     console.error('❌ Erro ao salvar pedido:', err);
//     return false;
//   }
// }
// 
// // =====================================================
// // FUNÇÕES DE FORNECEDORES
// // =====================================================
// 
export async function buscarFornecedoresComHistorico(materialIds: string[]) {
  try {
    if (!materialIds || materialIds.length === 0) {
      return [];
    }
    
    console.log('🔍 Buscando fornecedores com histórico para materiais:', materialIds.length);
    console.log('📋 IDs dos materiais:', materialIds.map(id => id.slice(0,8)));
    
    // Primeiro, vamos verificar se os materiais existem
    const { data: materiaisExistentes, error: erroMateriais } = await supabaseAcor
      .from('materiais_catalogo')
      .select('id, nome')
      .in('id', materialIds);
    
    if (erroMateriais) {
      console.warn('⚠️ Erro ao verificar materiais:', erroMateriais.message);
    } else {
      console.log(`✅ ${materiaisExistentes?.length || 0} materiais encontrados no catálogo`);
      if (materiaisExistentes && materiaisExistentes.length > 0) {
        materiaisExistentes.forEach((material: any) => {
          console.log(`📦 ${material.id.slice(0,8)}: ${material.nome}`);
        });
      }
    }
    
    const { data: historico, error } = await supabaseAcor
      .from('historico_precos')
      .select(`
        fornecedor_canonico_id,
        material_id,
        preco_unitario,
        quantidade_comprada,
        preco_total,
        data_compra,
        fornecedores_canonicos!inner(
          id,
          nome_canonico,
          categoria_fornecedor,
          cidade
        )
      `)
      .in('material_id', materialIds)
      .not('fornecedor_canonico_id', 'is', null)
      .order('data_compra', { ascending: false });
    
    if (error) {
      console.error('❌ Erro ao buscar histórico:', error);
      return [];
    }
    
    if (!historico || historico.length === 0) {
      console.log('ℹ️ Nenhum histórico encontrado para os materiais');
      
      // Vamos verificar se existe algum histórico para qualquer material
      const { data: historicoGeral, error: erroGeral } = await supabaseAcor
        .from('historico_precos')
        .select('material_id, fornecedor_canonico_id')
        .limit(5);
      
      if (!erroGeral && historicoGeral && historicoGeral.length > 0) {
        console.log('📊 Histórico geral disponível:', historicoGeral.length, 'registros');
        console.log('🔍 Exemplos de materiais no histórico:', historicoGeral.map(h => h.material_id.slice(0,8)));
      }
      
      return [];
    }
    
    console.log(`📊 ${historico.length} registros de histórico encontrados`);
    
    // Agrupar por fornecedor
    const fornecedoresMap = new Map();
    
    historico.forEach((item: any) => {
      const fornecedorId = item.fornecedor_canonico_id;
      const fornecedor = item.fornecedores_canonicos;
      
      if (!fornecedoresMap.has(fornecedorId)) {
        fornecedoresMap.set(fornecedorId, {
          id: fornecedorId,
          nome: fornecedor.nome_canonico,
          categoria: fornecedor.categoria_fornecedor,
          cidade: fornecedor.cidade,
          materiais_historico: new Set(),
          total_compras: 0,
          preco_medio: 0,
          valor_total_historico: 0,
          ultima_compra: null,
          precos_por_material: new Map()
        });
      }
      
      const fornecedorData = fornecedoresMap.get(fornecedorId);
      fornecedorData.materiais_historico.add(item.material_id);
      fornecedorData.total_compras++;
      
      // Armazenar preços por material (mais recente primeiro)
      if (!fornecedorData.precos_por_material.has(item.material_id)) {
        fornecedorData.precos_por_material.set(item.material_id, {
          preco_unitario: parseFloat(item.preco_unitario) || 0,
          quantidade_comprada: parseFloat(item.quantidade_comprada) || 1,
          preco_total: parseFloat(item.preco_total) || 0,
          data_compra: item.data_compra
        });
      }
      
      // Calcular preço médio
      if (item.preco_unitario) {
        const precoUnitario = parseFloat(item.preco_unitario) || 0;
        if (precoUnitario > 0) {
          const precoAtual = fornecedorData.preco_medio || 0;
          const totalCompras = fornecedorData.total_compras;
          fornecedorData.preco_medio = (precoAtual * (totalCompras - 1) + precoUnitario) / totalCompras;
        }
      }
      
      // Acumular valor total histórico
      if (item.preco_total) {
        fornecedorData.valor_total_historico += parseFloat(item.preco_total) || 0;
      } else if (item.preco_unitario && item.quantidade_comprada) {
        const precoUnitario = parseFloat(item.preco_unitario) || 0;
        const quantidade = parseFloat(item.quantidade_comprada) || 1;
        fornecedorData.valor_total_historico += precoUnitario * quantidade;
      }
      
      // Atualizar última compra
      if (!fornecedorData.ultima_compra || new Date(item.data_compra) > new Date(fornecedorData.ultima_compra)) {
        fornecedorData.ultima_compra = item.data_compra;
      }
    });
    
    const fornecedores = Array.from(fornecedoresMap.values()).map(f => ({
      ...f,
      materiais_historico: Array.from(f.materiais_historico),
      precos_por_material: Object.fromEntries(f.precos_por_material)
    }));
    
    console.log(`✅ ${fornecedores.length} fornecedores com histórico encontrados`);
    
    // Log detalhado dos fornecedores encontrados
    fornecedores.forEach(fornecedor => {
      console.log(`🏢 ${fornecedor.nome}: ${fornecedor.materiais_historico.length}/${materialIds.length} materiais, R$ ${fornecedor.valor_total_historico.toFixed(2)} total histórico`);
    });
    
    return fornecedores;
    
  } catch (err) {
    console.error('❌ Erro ao buscar fornecedores com histórico:', err);
    return [];
  }
}

// export async function buscarFornecedoresAgrupados(q: string) {
//   try {
//     console.log('🔍 Buscando fornecedores agrupados para:', q);
//     
//     // Primeiro tentar função RPC
//     const { data, error } = await supabaseAcor.rpc('fn_fornecedores_buscar', {
//       p_q: q || null
//     });
//     
//     if (!error && data && Array.isArray(data)) {
//       console.log(`✅ ${data.length} fornecedores encontrados via RPC`);
//       // Converter para formato esperado
//       return data.map((f: any) => ({
//         id_master: f.id,
//         nome_canonico: f.nome_canonico,
//         lojas_count: f.aliases?.length || 0,
//         lojas: f.aliases || []
//       }));
//     }
//     
//     if (error) {
//       console.warn('⚠️ Função fn_fornecedores_buscar falhou:', error.message);
//     }
//     
//     // Fallback: buscar diretamente nas tabelas
//     console.log('🔄 Tentando fallback: busca direta nas tabelas...');
//     
//     try {
//       // Buscar fornecedores canônicos
//       const { data: fornecedoresCanonicos, error: erroCanonicos } = await supabaseAcor
//         .from('fornecedores_canonicos')
//         .select('id, nome_canonico, categoria_fornecedor, cidade')
//         .ilike('nome_canonico', `%${q}%`)
//         .eq('ativo', true)
//         .limit(10);
//       
//       if (!erroCanonicos && fornecedoresCanonicos && fornecedoresCanonicos.length > 0) {
//         console.log(`✅ ${fornecedoresCanonicos.length} fornecedores canônicos encontrados`);
//         
//         // Para cada fornecedor canônico, buscar aliases
//         const fornecedoresComAliases = await Promise.all(
//           fornecedoresCanonicos.map(async (fornecedor: any) => {
//             const { data: aliases } = await supabaseAcor
//               .from('fornecedores_aliases')
//               .select('id, nome_original, eh_matriz')
//               .eq('fornecedor_canonico_id', fornecedor.id)
//               .limit(5);
//             
//             return {
//               id_master: fornecedor.id,
//               nome_canonico: fornecedor.nome_canonico,
//               categoria: fornecedor.categoria_fornecedor,
//               cidade: fornecedor.cidade,
//               lojas_count: aliases?.length || 0,
//               lojas: aliases?.map((alias: any) => ({
//                 id: alias.id,
//                 nome: alias.nome_original,
//                 eh_matriz: alias.eh_matriz
//               })) || []
//             };
//           })
//         );
//         
//         return fornecedoresComAliases;
//       }
//       
//       // Se não encontrou por nome canônico, tentar por aliases
//       const { data: aliases, error: erroAliases } = await supabaseAcor
//         .from('fornecedores_aliases')
//         .select(`
//           id,
//           nome_original,
//           eh_matriz,
//           fornecedores_canonicos!inner(
//             id,
//             nome_canonico,
//             categoria_fornecedor,
//             cidade
//           )
//         `)
//         .ilike('nome_original', `%${q}%`)
//         .limit(10);
//       
//       if (!erroAliases && aliases && aliases.length > 0) {
//         console.log(`✅ ${aliases.length} aliases encontrados`);
//         
//         // Agrupar por fornecedor canônico
//         const fornecedoresMap = new Map();
//         
//         aliases.forEach((alias: any) => {
//           const fornecedorCanonico = alias.fornecedores_canonicos as any;
//           const fornecedorId = fornecedorCanonico.id;
//           
//           if (!fornecedoresMap.has(fornecedorId)) {
//             fornecedoresMap.set(fornecedorId, {
//               id_master: fornecedorId,
//               nome_canonico: fornecedorCanonico.nome_canonico,
//               categoria: fornecedorCanonico.categoria_fornecedor,
//               cidade: fornecedorCanonico.cidade,
//               lojas_count: 0,
//               lojas: []
//             });
//           }
//           
//           const fornecedor = fornecedoresMap.get(fornecedorId);
//           fornecedor.lojas.push({
//             id: alias.id,
//             nome: alias.nome_original,
//             eh_matriz: alias.eh_matriz
//           });
//           fornecedor.lojas_count++;
//         });
//         
//         return Array.from(fornecedoresMap.values());
//       }
//       
//     } catch (err) {
//       console.warn('⚠️ Erro no fallback de fornecedores:', err);
//     }
//     
//     console.log('ℹ️ Nenhum fornecedor encontrado');
//     return [];
//     
//   } catch (err) {
//     console.error('❌ Erro ao buscar fornecedores:', err);
//     return [];
//   }
// }
// 
// // =====================================================
// // FUNÇÕES DE ESTATÍSTICAS (OPCIONAIS)
// // =====================================================
// 
export async function buscarEstatisticasCatalogo() {
  try {
    const { data, error } = await supabase.rpc('fn_catalogo_stats');

    if (error) {
      console.error('❌ buscarEstatisticasCatalogo erro:', error);
      return null;
    }

    return data;
  } catch (err) {
    console.error('❌ Erro ao buscar estatísticas:', err);
    return null;
  }
}

export async function buscarPrecosMaterial(materialId: string) {
  try {
    const { data, error } = await supabase.rpc('fn_material_precos', {
      p_material_id: materialId
    });

    if (error) {
      console.error('❌ buscarPrecosMaterial erro:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('❌ Erro ao buscar preços:', err);
    return [];
  }
}

// =====================================================
// FUNÇÕES DE VERIFICAÇÃO E DIAGNÓSTICO
// =====================================================

export async function testarCotacoes(requisicaoId: string) {
  try {
    console.log('🧪 Testando cotações para requisição:', requisicaoId.slice(0,8));
    
    // Teste 1: Verificar itens da requisição
    const itens = await listarItens(requisicaoId);
    console.log(`📋 Itens encontrados: ${itens.length}`);
    
    const itensComMaterial = itens.filter((item: any) => item.material_id);
    console.log(`🔗 Itens com material vinculado: ${itensComMaterial.length}`);
    
    if (itensComMaterial.length > 0) {
      console.log('📋 Exemplos de itens com material:', itensComMaterial.slice(0, 3));
    }
    
    // Teste 2: Verificar função RPC
    if (itensComMaterial.length > 0) {
      const materialIds = itensComMaterial.map((item: any) => item.material_id);
      console.log('🔄 Testando função fn_cotacao_simples...');
      
      const { data, error } = await supabaseAcor.rpc('fn_cotacao_simples', { 
        p_material_ids: materialIds 
      });
      
      if (error) {
        console.error('❌ Erro na função fn_cotacao_simples:', error);
      } else {
        console.log(`✅ Função fn_cotacao_simples retornou:`, data);
      }
    }
    
    // Teste 3: Verificar tabelas de fornecedores
    const tabelasFornecedores = [
      'fornecedores_canonicos',
      'fornecedores_aliases',
      'historico_precos'
    ];
    
    for (const tabela of tabelasFornecedores) {
      try {
        console.log(`🧪 Testando tabela: ${tabela}`);
        
        const { data: dados, error } = await supabaseAcor
          .from(tabela)
          .select('*')
          .limit(1);
        
        if (error) {
          console.warn(`⚠️ Tabela ${tabela} não disponível:`, error.message);
        } else {
          console.log(`✅ Tabela ${tabela} disponível, ${dados?.length || 0} registros`);
        }
      } catch (err) {
        console.warn(`⚠️ Erro ao testar ${tabela}:`, err);
      }
    }
    
    // Teste 4: Verificar histórico de preços para materiais específicos
    if (itensComMaterial.length > 0) {
      const materialIds = itensComMaterial.map((item: any) => item.material_id);
      console.log('🧪 Testando histórico de preços...');
      
      const { data: historico, error: erroHistorico } = await supabaseAcor
        .from('historico_precos')
        .select(`
          material_id,
          fornecedor_canonico_id,
          preco_unitario,
          quantidade_comprada,
          preco_total,
          data_compra,
          fornecedores_canonicos!inner(nome_canonico)
        `)
        .in('material_id', materialIds.slice(0, 3)) // Testar apenas os primeiros 3 materiais
        .limit(10);
      
      if (erroHistorico) {
        console.warn('⚠️ Erro ao buscar histórico de preços:', erroHistorico.message);
      } else {
        console.log(`✅ ${historico?.length || 0} registros de histórico encontrados`);
        if (historico && historico.length > 0) {
          console.log('📋 Exemplos de histórico:', historico.slice(0, 2));
          
          // Calcular estatísticas
          const totalValor = historico.reduce((sum, item) => sum + (item.preco_total || 0), 0);
          const precoMedio = historico.length > 0 ? totalValor / historico.length : 0;
          console.log(`💰 Estatísticas: Total R$ ${totalValor.toFixed(2)}, Média R$ ${precoMedio.toFixed(2)}`);
        }
      }
    }
    
    // Teste 5: Testar cálculo de valores
    if (itensComMaterial.length > 0) {
      const materialIds = itensComMaterial.map((item: any) => item.material_id);
      console.log('🧪 Testando cálculo de valores...');
      
      // Buscar um fornecedor para teste
      const { data: fornecedorTeste } = await supabaseAcor
        .from('fornecedores_canonicos')
        .select('id, nome_canonico')
        .eq('ativo', true)
        .limit(1)
        .single();
      
      if (fornecedorTeste) {
        const valorTotal = await calcularValorTotalEstimado(materialIds, fornecedorTeste.id);
        console.log(`💰 Valor total estimado para ${fornecedorTeste.nome_canonico}: R$ ${valorTotal.toFixed(2)}`);
      }
    }
    
    return {
      itens_total: itens.length,
      itens_com_material: itensComMaterial.length,
      material_ids: itensComMaterial.map((item: any) => item.material_id)
    };
    
  } catch (error) {
    console.error('❌ Erro ao testar cotações:', error);
    return null;
  }
}

export async function testarPedidoWhatsApp(requisicaoId: string, fornecedorId?: string) {
  try {
    console.log('🧪 Testando PedidoWhatsApp para requisição:', requisicaoId.slice(0,8));
    
    // Teste 1: Verificar detalhes da requisição
    const detalhesRequisicao = await buscarDetalhesRequisicao(requisicaoId);
    console.log('📋 Detalhes da requisição:', detalhesRequisicao ? 'OK' : 'Não encontrado');
    
    // Teste 2: Verificar itens da requisição
    const itens = await listarItens(requisicaoId);
    console.log(`📦 ${itens.length} itens encontrados na requisição`);
    
    // Teste 3: Verificar função RPC
    if (fornecedorId) {
      console.log('🔄 Testando função fn_pedido_whatsapp...');
      
      const { data, error } = await supabase.rpc('fn_pedido_whatsapp', { 
        p_requisicao: requisicaoId, 
        p_fornecedor: fornecedorId 
      });
      
      if (error) {
        console.error('❌ Erro na função fn_pedido_whatsapp:', error);
      } else {
        console.log('✅ Função fn_pedido_whatsapp retornou:', data ? 'Dados' : 'Vazio');
      }
    }
    
    // Teste 4: Verificar tabelas de fornecedores
    const tabelasFornecedores = [
      'fornecedores_canonicos',
      'fornecedores_aliases',
      'compras_pedidos'
    ];
    
    for (const tabela of tabelasFornecedores) {
      try {
        console.log(`🧪 Testando tabela: ${tabela}`);
        
        const { data: dados, error } = await supabaseAcor
          .from(tabela)
          .select('*')
          .limit(1);
        
        if (error) {
          console.warn(`⚠️ Tabela ${tabela} não disponível:`, error.message);
        } else {
          console.log(`✅ Tabela ${tabela} disponível, ${dados?.length || 0} registros`);
        }
      } catch (err) {
        console.warn(`⚠️ Erro ao testar ${tabela}:`, err);
      }
    }
    
    // Teste 5: Gerar mensagem de teste
    if (fornecedorId) {
      console.log('🔄 Testando geração de mensagem...');
      
      const mensagem = await gerarMensagemPedido(requisicaoId, fornecedorId);
      console.log('✅ Mensagem gerada:', mensagem ? `${mensagem.length} caracteres` : 'Vazia');
      
      if (mensagem) {
        console.log('📋 Preview da mensagem:', mensagem.substring(0, 200) + '...');
      }
    }
    
    return {
      requisicao_id: requisicaoId,
      detalhes_ok: !!detalhesRequisicao,
      itens_count: itens.length,
      fornecedor_id: fornecedorId
    };
    
  } catch (error) {
    console.error('❌ Erro ao testar PedidoWhatsApp:', error);
    return null;
  }
}

export async function listarTabelasDisponiveis() {
  try {
    console.log('🔍 Listando tabelas disponíveis no schema acor...');
    
    // Tentar algumas tabelas conhecidas
    const tabelasConhecidas = [
      'compras_requisicoes',
      'compras_requisicao_itens',
      'vw_compras_painel',
      'vw_compras_requisicao_itens',
      'vw_materiais_busca',
      'vw_materiais_completos',
      'materiais_canonicos',
      'catalogo_materiais',
      'produtos',
      'materiais',
      'fornecedores_canonicos',
      'fornecedores_aliases',
      'historico_precos'
    ];
    
    const resultados: Record<string, { existe: boolean; erro?: string }> = {};
    
    for (const tabela of tabelasConhecidas) {
      try {
        const { error } = await supabaseAcor
          .from(tabela)
          .select('*')
          .limit(1);
        
        if (error) {
          if (error.message.includes('does not exist')) {
            resultados[tabela] = { existe: false, erro: 'Tabela não existe' };
          } else if (error.message.includes('permission denied')) {
            resultados[tabela] = { existe: true, erro: 'Permissão negada' };
          } else {
            resultados[tabela] = { existe: true, erro: error.message };
          }
        } else {
          resultados[tabela] = { existe: true };
        }
        
      } catch (err: any) {
        resultados[tabela] = { existe: false, erro: err.message };
      }
    }
    
    console.log('📊 Tabelas disponíveis:', resultados);
    return resultados;
  } catch (error) {
    console.error('❌ Erro ao listar tabelas:', error);
    return {};
  }
}

export async function testarViewMateriais() {
  try {
    console.log('🧪 Testando views de materiais...');
    
    const views = [
      { nome: 'vw_materiais_busca', descricao: 'View específica para busca de materiais' },
      { nome: 'vw_materiais_completos', descricao: 'View completa de materiais' }
    ];
    
    const resultados: Record<string, { existe: boolean; erro?: string; materiais?: any[]; total?: number }> = {};
    
    for (const view of views) {
      try {
        console.log(`🧪 Testando view: ${view.nome} (${view.descricao})`);
        
            
        console.log(`✅ View ${view.nome} existe`);
        
        // Teste 2: Buscar alguns materiais
        const { data: materiais, error: erroBusca } = await supabaseAcor
          .from(view.nome)
          .select('id, nome, unidade_padrao, categoria')
          .limit(5);
        
        if (erroBusca) {
          console.error(`❌ Erro ao buscar materiais em ${view.nome}:`, erroBusca.message);
          resultados[view.nome] = { existe: true, erro: erroBusca.message, materiais: [] };
        } else {
          console.log(`✅ ${materiais?.length || 0} materiais encontrados na view ${view.nome}`);
          console.log('📋 Exemplos:', materiais?.slice(0, 3));
          
          resultados[view.nome] = { 
            existe: true, 
            materiais: materiais || [],
            total: materiais?.length || 0
          };
        }
        
      } catch (error) {
        console.error(`❌ Erro ao testar view ${view.nome}:`, error);
        resultados[view.nome] = { existe: false, erro: String(error) };
      }
    }
    
    console.log('📊 Resultados dos testes de views:', resultados);
    return resultados;
    
  } catch (error) {
    console.error('❌ Erro ao testar views:', error);
    return { erro: String(error) };
  }
}

export async function testarFuncoesRPC() {
  const funcoes = [
    'fn_materiais_sugerir',
    'fn_requisicao_criar', 
    'fn_item_add',
    'fn_item_update_json',
    'fn_item_delete',
    'fn_requisicao_update_json',
    'fn_requisicao_detalhes',
    'fn_requisicao_parse_text'
  ];
  
  const resultados: Record<string, { existe: boolean; erro?: string }> = {};
  
  for (const funcao of funcoes) {
    try {
      console.log(`🧪 Testando função: ${funcao}`);
      
      // Teste simples - apenas verificar se a função existe
      const { error } = await supabase.rpc(funcao as any, {});
      
      if (error) {
        if (error.message.includes('does not exist')) {
          resultados[funcao] = { existe: false, erro: 'Função não existe' };
        } else if (error.message.includes('forbidden')) {
          resultados[funcao] = { existe: true, erro: 'Permissão negada' };
        } else {
          resultados[funcao] = { existe: true, erro: error.message };
        }
      } else {
        resultados[funcao] = { existe: true };
      }
      
    } catch (err: any) {
      resultados[funcao] = { existe: false, erro: err.message };
    }
  }
  
  console.log('📊 Resultados dos testes RPC:', resultados);
  return resultados;
}

export async function testarTabelas() {
  const tabelas = [
    'compras_requisicoes',
    'compras_requisicao_itens',
    'vw_compras_painel'
  ];
  
  const resultados: Record<string, { acessivel: boolean; erro?: string }> = {};
  
  for (const tabela of tabelas) {
    try {
      console.log(`🧪 Testando tabela: ${tabela}`);
      
      // Teste simples - apenas verificar se consegue fazer select
      const { error } = await supabaseAcor
        .from(tabela)
        .select('*')
        .limit(1);
      
      if (error) {
        if (error.message.includes('does not exist')) {
          resultados[tabela] = { acessivel: false, erro: 'Tabela não existe' };
        } else if (error.message.includes('permission denied')) {
          resultados[tabela] = { acessivel: false, erro: 'Permissão negada' };
        } else {
          resultados[tabela] = { acessivel: false, erro: error.message };
        }
      } else {
        resultados[tabela] = { acessivel: true };
      }
      
    } catch (err: any) {
      resultados[tabela] = { acessivel: false, erro: err.message };
    }
  }
  
  console.log('📊 Resultados dos testes de tabelas:', resultados);
  return resultados;
}

export async function verificarConectividade() {
  try {
    console.log('🔍 Verificando conectividade...');
    
    // Testar view principal primeiro
    const painel = await listarPainelCompras();
    if (Array.isArray(painel)) {
      console.log('✅ View vw_compras_painel OK');
      return { 
        sucesso: true, 
        painel_count: painel.length,
        timestamp: new Date().toISOString()
      };
    }
    
    return { 
      sucesso: false, 
      erro: 'View vw_compras_painel não acessível',
      sugestao: 'Verificar permissões e schema'
    };
    
  } catch (error) {
    console.error('❌ Erro de conectividade:', error);
    return { 
      sucesso: false, 
      erro: 'Erro de conexão geral',
      sugestao: 'Verificar configuração do Supabase'
    };
  }
}

export async function diagnosticarSistema() {
  try {
    console.log('🩺 Iniciando diagnóstico...');
    
    const diagnostico = {
      timestamp: new Date().toISOString(),
      funcoes_testadas: {} as Record<string, boolean>,
      conectividade: false,
      views_acessiveis: [] as string[]
    };
    
      
    // Testar funções principais
    const funcoesTestar = [
      'fn_materiais_sugerir',
      'fn_requisicao_criar',
      'fn_item_add'
    ];
    
    for (const funcao of funcoesTestar) {
      try {
        const { error } = await supabase.rpc(funcao as any, {});
        diagnostico.funcoes_testadas[funcao] = !error || !error.message.includes('does not exist');
      } catch {
        diagnostico.funcoes_testadas[funcao] = false;
      }
    }
    
    // Verificar conectividade geral
    const conectividade = await verificarConectividade();
    diagnostico.conectividade = conectividade.sucesso;
    
    console.log('📊 Diagnóstico completo:', diagnostico);
    return diagnostico;
  } catch (error) {
    console.error('❌ Erro no diagnóstico:', error);
    return null;
  }
}

// =====================================================
// FUNÇÕES AUXILIARES COMPATIBILIDADE (MANTIDAS)
// =====================================================

// Mantidas para compatibilidade com outros arquivos que ainda podem usar
export async function setItemMaterial(itemId: string, materialId: string) {
  return await updateItemCampos(itemId, { material_id: materialId });
}

export async function buscarMateriaisComContexto(query: string, limite: number = 10) {
  // Versão simplificada - apenas chama buscarMateriais
  const materiais = await buscarMateriais(query);
  return materiais.slice(0, limite);
}

// =====================================================
// EXPORT DE COMPATIBILIDADE
// =====================================================

// Funções já estão exportadas acima, não é necessário re-exportar

// =====================================================
// LOGGING E DEBUG (DESENVOLVIMENTO)
// =====================================================

// Declaração de tipo para window.comprasDebug será feita após as definições das funções

// @ts-ignore
window.comprasDebug = {
  ...(window.comprasDebug ?? {}),
  async peekRequisicaoItens(reqId: string) {
    const itens = await listarItens(reqId);
    console.log('📦 itens requisicao:', itens);
    return itens;
  },
  async peekHistorico(materialId: string, janelaDias = 365) {
    const x = await listarHistoricoSimples([materialId], [], janelaDias);
    console.log('🧾 historico (simples):', x);
    return x;
  },
  async dumpComparativo(reqId: string) {
    const itens = await listarItens(reqId);
    const materialIds = itens.map((i: any) => i.material_id).filter(Boolean) as string[];

    const fornecedores = await buscarFornecedoresComHistoricoSimples(materialIds);
    const ofertas = await listarHistoricoSimples(
      materialIds,
      fornecedores.map((f: any) => f.fornecedor_id)
    );

    const resumo = { fornecedores, itens, ofertas };
    console.log('📊 dump comparativo (simples):', resumo);
    return resumo;
  }
};

// Função de teste específica para cotações
export async function testarValoresCotacoes(requisicaoId: string) {
  try {
    console.log('🧪 Testando valores de cotações para requisição:', requisicaoId.slice(0,8));
    
    // Buscar itens da requisição
    const itens = await listarItens(requisicaoId);
    const materialIds = itens
      .filter((item: any) => item.material_id)
      .map((item: any) => item.material_id);
    
    console.log(`📋 ${materialIds.length} materiais encontrados`);
    
    if (materialIds.length === 0) {
      console.log('⚠️ Nenhum material vinculado encontrado');
      return null;
    }
    
    // Buscar fornecedores com histórico
    const fornecedores = await buscarFornecedoresComHistorico(materialIds);
    console.log(`🏢 ${fornecedores.length} fornecedores com histórico encontrados`);
    
    // Testar cálculo de valores para cada fornecedor
    const resultados = await Promise.all(
      fornecedores.map(async (fornecedor: any) => {
        const valorTotal = await calcularValorTotalEstimado(materialIds, fornecedor.id);
        
        return {
          fornecedor_id: fornecedor.id,
          fornecedor_nome: fornecedor.nome,
          materiais_historico: fornecedor.materiais_historico.length,
          total_materiais: materialIds.length,
          valor_total: valorTotal,
          valor_total_historico: fornecedor.valor_total_historico,
          preco_medio: fornecedor.preco_medio
        };
      })
    );
    
    console.log('📊 Resultados dos testes de valores:');
    resultados.forEach(resultado => {
      console.log(`🏢 ${resultado.fornecedor_nome}: R$ ${resultado.valor_total.toFixed(2)} (${resultado.materiais_historico}/${resultado.total_materiais} materiais)`);
    });
    
    return resultados;
    
  } catch (error) {
    console.error('❌ Erro ao testar valores de cotações:', error);
    return null;
  }
}

// Função de teste para verificar materiais e histórico
export async function testarMateriaisEHistorico(requisicaoId: string) {
  try {
    console.log('🧪 Testando materiais e histórico para requisição:', requisicaoId.slice(0,8));
    
    // 1. Buscar itens da requisição
    const itens = await listarItens(requisicaoId);
    console.log(`📋 ${itens.length} itens encontrados na requisição`);
    
    const itensComMaterial = itens.filter((item: any) => item.material_id);
    const materialIds = itensComMaterial.map((item: any) => item.material_id);
    
    console.log(`🔗 ${materialIds.length} materiais vinculados encontrados`);
    
    if (materialIds.length === 0) {
      console.log('⚠️ Nenhum material vinculado encontrado');
      return null;
    }
    
    // 2. Verificar se os materiais existem no catálogo
    console.log('🔍 Verificando materiais no catálogo...');
    const { data: materiaisCatalogo, error: erroCatalogo } = await supabaseAcor
      .from('materiais_catalogo')
      .select('id, nome, ativo')
      .in('id', materialIds);
    
    if (erroCatalogo) {
      console.warn('⚠️ Erro ao buscar catálogo:', erroCatalogo.message);
    } else {
      console.log(`✅ ${materiaisCatalogo?.length || 0} materiais encontrados no catálogo`);
      if (materiaisCatalogo && materiaisCatalogo.length > 0) {
        materiaisCatalogo.forEach((material: any) => {
          console.log(`📦 ${material.id.slice(0,8)}: ${material.nome} (ativo: ${material.ativo})`);
        });
      }
    }
    
    // 3. Verificar histórico de preços para estes materiais
    console.log('🔍 Verificando histórico de preços...');
    const { data: historico, error: erroHistorico } = await supabaseAcor
      .from('historico_precos')
      .select(`
        material_id,
        fornecedor_canonico_id,
        preco_unitario,
        quantidade_comprada,
        preco_total,
        data_compra,
        fornecedores_canonicos!inner(nome_canonico)
      `)
      .in('material_id', materialIds)
      .limit(10);
    
    if (erroHistorico) {
      console.warn('⚠️ Erro ao buscar histórico:', erroHistorico.message);
    } else {
      console.log(`📊 ${historico?.length || 0} registros de histórico encontrados`);
      if (historico && historico.length > 0) {
        historico.forEach((item: any) => {
          console.log(`💰 ${item.material_id.slice(0,8)} - ${item.fornecedores_canonicos.nome_canonico}: R$ ${item.preco_unitario} x ${item.quantidade_comprada} = R$ ${item.preco_total || (item.preco_unitario * item.quantidade_comprada)}`);
        });
      }
    }
    
    // 4. Verificar fornecedores ativos
    console.log('🔍 Verificando fornecedores ativos...');
    const { data: fornecedoresAtivos, error: erroFornecedores } = await supabaseAcor
      .from('fornecedores_canonicos')
      .select('id, nome_canonico, categoria_fornecedor, cidade')
      .eq('ativo', true)
      .limit(5);
    
    if (erroFornecedores) {
      console.warn('⚠️ Erro ao buscar fornecedores:', erroFornecedores.message);
    } else {
      console.log(`🏢 ${fornecedoresAtivos?.length || 0} fornecedores ativos encontrados`);
      if (fornecedoresAtivos && fornecedoresAtivos.length > 0) {
        fornecedoresAtivos.forEach((fornecedor: any) => {
          console.log(`🏢 ${fornecedor.id.slice(0,8)}: ${fornecedor.nome_canonico} (${fornecedor.cidade})`);
        });
      }
    }
    
    // 5. Testar busca de fornecedores com histórico
    console.log('🔍 Testando busca de fornecedores com histórico...');
    const fornecedoresComHistorico = await buscarFornecedoresComHistorico(materialIds);
    console.log(`✅ ${fornecedoresComHistorico.length} fornecedores com histórico encontrados`);
    
    return {
      itens: itens.length,
      materiaisVinculados: materialIds.length,
      materiaisCatalogo: materiaisCatalogo?.length || 0,
      historico: historico?.length || 0,
      fornecedoresAtivos: fornecedoresAtivos?.length || 0,
      fornecedoresComHistorico: fornecedoresComHistorico.length
    };
    
  } catch (error) {
    console.error('❌ Erro ao testar materiais e histórico:', error);
    return null;
  }
}

// Função para estimar valor quando não há histórico específico
async function estimarValorSemHistorico(materialIds: string[], fornecedorId: string) {
  try {
    if (!materialIds || materialIds.length === 0) {
      return 0;
    }
    
    console.log(`💰 Estimando valor para fornecedor ${fornecedorId.slice(0,8)} sem histórico específico`);
    
    // Buscar preços médios do mercado para estes materiais
    const { data: precosMedios, error } = await supabaseAcor
      .from('historico_precos')
      .select(`
        material_id,
        preco_unitario,
        quantidade_comprada,
        preco_total
      `)
      .in('material_id', materialIds)
      .not('preco_unitario', 'is', null)
      .order('data_compra', { ascending: false })
      .limit(100); // Buscar mais registros para calcular média
    
    if (error) {
      console.warn('⚠️ Erro ao buscar preços médios:', error.message);
      return 0;
    }
    
    if (!precosMedios || precosMedios.length === 0) {
      console.log('ℹ️ Nenhum preço médio encontrado para estimativa');
      return 0;
    }
    
    // Calcular preços médios por material
    const precosPorMaterial = new Map<string, number[]>();
    
    precosMedios.forEach((item: any) => {
      const materialId = item.material_id;
      const precoUnitario = parseFloat(item.preco_unitario) || 0;
      
      if (!precosPorMaterial.has(materialId)) {
        precosPorMaterial.set(materialId, []);
      }
      
      if (precoUnitario > 0) {
        precosPorMaterial.get(materialId)!.push(precoUnitario);
      }
    });
    
    // Calcular valor total estimado
    let valorTotal = 0;
    let materiaisComPreco = 0;
    
    for (const materialId of materialIds) {
      const precos = precosPorMaterial.get(materialId);
      
      if (precos && precos.length > 0) {
        // Calcular média dos preços para este material
        const precoMedio = precos.reduce((sum: number, preco: number) => sum + preco, 0) / precos.length;
        valorTotal += precoMedio;
        materiaisComPreco++;
        console.log(`📦 Material ${materialId.slice(0,8)}: R$ ${precoMedio.toFixed(2)} (média de ${precos.length} preços)`);
      } else {
        // Se não há preços para este material, usar preço médio geral
        const precoMedioGeral = valorTotal > 0 ? valorTotal / materiaisComPreco : 100; // R$ 100 como padrão
        valorTotal += precoMedioGeral;
        console.log(`📦 Material ${materialId.slice(0,8)}: R$ ${precoMedioGeral.toFixed(2)} (preço médio geral)`);
      }
    }
    
    console.log(`💰 Valor total estimado para fornecedor ${fornecedorId.slice(0,8)}: R$ ${valorTotal.toFixed(2)}`);
    return valorTotal;
    
  } catch (err) {
    console.warn('⚠️ Erro ao estimar valor sem histórico:', err);
    return 0;
  }
}

// =====================================================
// FUNÇÕES DE HISTÓRICO E TRIPLA DE MATERIAIS
// =====================================================

type Tripla = { tipo_norm: string; classe_norm: string; dn_norm: string };
type HistoricoLinha = {
  data: string; // ISO
  fornecedor_nome: string;
  fornecedor_canonico_id: string | null;
  nome_produto: string | null;
  preco_unitario: number | string;
  material_id: string | null;
};

export async function getTriplaDoMaterial(materialId: string): Promise<Tripla | null> {
  const { data, error } = await supabase
    .from('materiais_catalogo')
    .select('tipo_norm, classe_norm, dn_norm')
    .eq('id', materialId)
    .single();
  if (error) {
    console.error('❌ getTriplaDoMaterial:', error);
    return null;
  }
  return data as Tripla;
}

/**
 * Busca histórico *sem marca* pela tripla.
 * Limita por período e quantidade para evitar timeouts.
 */
export async function listarHistoricoSemMarcaPorMaterial(
  materialId: string,
  opts?: { dias?: number; limit?: number }
) {
  const { dias = 365, limit = 500 } = opts || {};

  const tripla = await getTriplaDoMaterial(materialId);
  if (!tripla) return { linhas: [] as HistoricoLinha[], fornecedores: [] as { id: string | null; nome: string }[], latestPorForn: [] as { fornecedor: { id: string | null; nome: string }, ultima?: { data: string; preco: number; rotulo?: string } }[] };

  const dataMin = format(subDays(new Date(), dias), 'yyyy-MM-dd');

  // IMPORTANTE: a view vw_stg_to_historico_mapeado já contém tipo_norm/classe_norm/dn_norm
  const { data, error } = await supabase
    .from('vw_stg_to_historico_mapeado')
    .select('data, fornecedor_nome, fornecedor_canonico_id, material_id, nome_produto, preco_unitario')
    .eq('tipo_norm', tripla.tipo_norm)
    .eq('classe_norm', tripla.classe_norm)
    .eq('dn_norm', tripla.dn_norm)
    .gte('data', dataMin)
    .order('data', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('❌ listarHistoricoSemMarcaPorMaterial:', error);
    return { linhas: [], fornecedores: [], latestPorForn: [] };
  }

  const linhas = (data || []) as HistoricoLinha[];

  // Agrupa por fornecedor e pega o último preço
  const fornKey = (r: HistoricoLinha) => r.fornecedor_canonico_id ?? r.fornecedor_nome;
  const mapF = new Map<string, { id: string | null; nome: string }>();
  const ofertas: Record<string, { data: string; preco: number; rotulo?: string }[]> = {};
  for (const r of linhas) {
    const key = fornKey(r);
    if (!mapF.has(key)) mapF.set(key, { id: r.fornecedor_canonico_id ?? null, nome: r.fornecedor_nome });
    const preco = typeof r.preco_unitario === 'string' ? Number(r.preco_unitario) : (r.preco_unitario ?? 0);
    if (!ofertas[key]) ofertas[key] = [];
    ofertas[key].push({ data: r.data, preco, rotulo: r.nome_produto ?? undefined });
  }

  const fornecedores = [...mapF.values()];
  const latestPorForn = fornecedores.map(f => {
    const key = f.id ?? f.nome;
    const arr = (ofertas[key] || []).sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
    return { fornecedor: f, ultima: arr[0] };
  });

  return { linhas, fornecedores, latestPorForn };
}

// (Opcional) helpers de debug no console
// chame em qualquer lugar uma vez, p.ex. no AppInit:
export function instalarDebugCompras() {
  // @ts-ignore
  window.comprasDebug = {
    async peekTripla(materialId: string) {
      const t = await getTriplaDoMaterial(materialId);
      console.log('🔎 tripla:', t);
      return t;
    },
    async peekHistoricoSemMarca(materialId: string, dias = 365) {
      const r = await listarHistoricoSemMarcaPorMaterial(materialId, { dias, limit: 500 });
      console.log('🧾 historico sem marca:', r);
      return r;
    },
  };
  console.log('🛠️ Debug compras disponível: window.comprasDebug');
}

// =====================================================
// NOVAS FUNÇÕES PARA COTAÇÃO COMPARATIVA (matriz)
// =====================================================

// Lista os pedidos (colunas) de uma requisição
export async function listarPedidosDaRequisicao(requisicaoId: string) {
  const { data, error } = await supabaseAcor
    .from('compras_pedidos')
    .select(`id, fornecedor_id, status`)
    .eq('requisicao_id', requisicaoId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Garante a criação do header de pedido para um fornecedor (coluna)
export async function ensurePedido(requisicaoId: string, fornecedorId: string) {
  const { data: exists, error: err1 } = await supabaseAcor
    .from('compras_pedidos')
    .select('id')
    .eq('requisicao_id', requisicaoId)
    .eq('fornecedor_id', fornecedorId)
    .limit(1);
  if (err1) throw err1;
  if (exists && exists.length) return exists[0].id as string;

  const detalhes = await buscarDetalhesRequisicao(requisicaoId);
  const { data, error } = await supabaseAcor
    .from('compras_pedidos')
    .insert({
      requisicao_id: requisicaoId,
      fornecedor_id: fornecedorId,
      obra_id: detalhes?.obra_id ?? null,
      status: 'Orçando'
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id as string;
}

// Tipagem para upsert da célula (pedido_id x requisicao_item_id)
export type PedidoItemUpsert = {
  pedido_id: string;
  requisicao_item_id: string;
  preco_unitario?: number | null;
  unidade_ofertada?: string | null;
  fator_conversao?: number | null;
  quantidade_ofertada?: number | null;
  marca_ofertada?: string | null;
  descricao_ofertada?: string | null;
  prazo_entrega_dias?: number | null;
  disponivel?: boolean | null;
  material_id?: string | null;
};

// Upsert de célula na matriz (oferta do fornecedor para um item)
export async function upsertPedidoItem(cel: PedidoItemUpsert) {
  const { error } = await supabaseAcor
    .from('compras_pedidos_itens')
    .upsert(cel, { onConflict: 'pedido_id,requisicao_item_id' });
  if (error) throw error;
  return true;
}

// Matriz comparativa (linhas = itens, colunas = fornecedores)
export async function listarComparativo(requisicaoId: string) {
  const { data, error } = await supabaseAcor
    .from('vw_cotacao_comparativo')
    .select('*')
    .eq('requisicao_id', requisicaoId);
  if (error) throw error;
  return data || [];
}

// Rodapé por fornecedor (cobertura, total, prazo)
export async function listarResumoFornecedores(requisicaoId: string) {
  const { data, error } = await supabaseAcor
    .from('vw_cotacao_resumo_fornecedor')
    .select('*')
    .eq('requisicao_id', requisicaoId)
    .order('itens_cobertos', { ascending: false })
    .order('valor_total', { ascending: true }); // ⇦ acrescentar este
  if (error) throw error;
  return data || [];
}

// Fecha a cotação para um fornecedor (gera histórico e marca 'Feito')
export async function fecharCotacao(requisicaoId: string, pedidoId: string) {
  const { data, error } = await supabase.rpc('fn_cotacao_fechar', {
    p_requisicao_id: requisicaoId,
    p_pedido_id: pedidoId,
  });
  if (error) throw error;
  return data;
}

// === FUNÇÕES DE CONSULTA OTIMIZADA ===

async function _queryHistoricoFast(materialIds: string[], sinceISO: string) {
  const { data, error } = await supabaseAcor
    .schema('acor')
    .from('mv_hist_precos') // ⚡ rápido
    .select('material_id, fornecedor_canonico_id, fornecedor_nome, preco_unitario, data')
    .in('material_id', materialIds)
    .gte('data', sinceISO)
    .order('data', { ascending: false });

  // Se a MV não existir ainda: erro 42P01 → quem usa Supabase vê como { code: '42P01', ... }
  if (error && (error as any).code === '42P01') return { data: null, error: null };
  if (error) throw error;
  return { data, error: null };
}

async function _queryHistoricoView(materialIds: string[], sinceISO: string) {
  // ⚠️ fallback (lento). Use só se a MV não existir ainda.
  const { data, error } = await supabaseAcor
    .schema('acor')
    .from('vw_stg_to_historico_mapeado')
    .select('material_id, fornecedor_canonico_id, fornecedor_nome, preco_unitario, data')
    .in('material_id', materialIds)
    .gt('preco_unitario', 0)
    .gte('data', sinceISO)
    .order('data', { ascending: false })
    .limit(2000); // pequeno para evitar timeout

  if (error) throw error;
  return { data, error: null };
}

// === COMPARATIVO BÁSICO POR HISTÓRICO (sem fallbacks/IA) ===
export async function listarComparativoHistoricoBasico(requisicaoId: string) {
  console.groupCollapsed('🟦 listarComparativoHistoricoBasico');
  try {
    console.debug('requisicaoId:', requisicaoId);
    const itensRaw = await listarItens(requisicaoId);
    console.debug('itens da requisição:', itensRaw.length, itensRaw);

    // normaliza possíveis nomes de campo
    const itens = itensRaw.map((it: any) => ({
      ...it,
      material_id: it.material_id ?? null,
    }));

    const materialIds: string[] = [...new Set(
      itens.map((i: any) => i.material_id).filter(Boolean)
    )] as string[];

    console.debug('materialIds (únicos):', materialIds);

    if (materialIds.length === 0) {
      console.warn('⚠️ Nenhum item com material_id nesta requisição.');
      return { fornecedores: [], itens, ofertas: {} as Record<string, any> };
    }

    const dias = 720; // 24 meses pra garantir volume (ajuste depois)
    const since = new Date(Date.now() - dias * 24 * 3600 * 1000).toISOString().slice(0, 10);
    console.debug('janela (desde):', since);

    // 1ª tentativa: MV rápida
    let data: any[] | null = null;
    console.debug('🔄 Tentando consulta rápida (MV)...');
    {
      const r = await _queryHistoricoFast(materialIds, since);
      data = r.data;
      if (data) {
        console.debug('✅ MV encontrada, dados carregados:', data.length);
      } else {
        console.debug('⚠️ MV não disponível, tentando view...');
      }
    }

    // fallback: view (pode ser lenta)
    if (!data) {
      console.debug('🔄 Usando fallback (view)...');
      const r = await _queryHistoricoView(materialIds, since);
      data = r.data;
      console.debug('📊 Dados carregados via view:', data?.length ?? 0);
    }

    console.debug('linhas de histórico retornadas:', data?.length ?? 0);
    if (!data || data.length === 0) {
      return { fornecedores: [], itens, ofertas: {} as Record<string, any> };
    }

    // Mantém a linha MAIS RECENTE por (material_id, fornecedor)
    type Row = { material_id: string; fornecedor_canonico_id: string | null; fornecedor_nome: string; preco_unitario: number; data: string };
    const latest = new Map<string, Row>(); // key = material_id + '|' + fornecedorKey
    const fornecedorNomePorKey = new Map<string, string>(); // fornecedorKey -> nome

    for (const r of data as Row[]) {
      if (!r.material_id || !r.fornecedor_nome || !r.preco_unitario) continue;
      const fornecedorKey = r.fornecedor_canonico_id && r.fornecedor_canonico_id !== ''
        ? r.fornecedor_canonico_id
        : `NID::${r.fornecedor_nome.toLowerCase()}`;

      const k = `${r.material_id}|${fornecedorKey}`;
      if (!latest.has(k)) {
        latest.set(k, r);
        if (!fornecedorNomePorKey.has(fornecedorKey)) {
          fornecedorNomePorKey.set(fornecedorKey, r.fornecedor_nome);
        }
      }
    }

    // monta o objeto ofertas por itemId (da requisição) x fornecedor
    const ofertas: Record<string, Record<string, { nome: string; preco: number; data: string }>> = {};
    for (const it of itens) {
      ofertas[it.id] = {};
      if (!it.material_id) continue;

      for (const [k, r] of latest) {
        const [matId, fornKey] = k.split('|');
        if (matId !== it.material_id) continue;

        ofertas[it.id][fornKey] = {
          nome: fornecedorNomePorKey.get(fornKey) || 'Fornecedor',
          preco: r.preco_unitario,
          data: r.data,
        };
      }
    }

    const fornecedores = [...fornecedorNomePorKey.entries()]
      .map(([key, nome]) => ({ key, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

    console.debug('fornecedores finais:', fornecedores);
    console.debug('ofertas por itemId:', ofertas);

    return { fornecedores, itens, ofertas };
  } finally {
    console.groupEnd();
  }
}

// =====================================================
// THREAD E HISTÓRICO DE REQUISIÇÕES
// =====================================================

// THREAD da requisição
// ensureThreadForRequisicao não é mais necessário: a RPC de comentar garante a thread.

// HISTÓRICO (logs) - tipo já declarado acima

export async function listarLogsRequisicao(requisicaoId: string) {
  const { data, error } = await supabase
    .schema('acor')
    .from('compras_requisicoes_logs')
    .select(`
      acao,
      campo,
      old,
      new,
      meta,
      changed_at,
      changed_by
    `)
    .eq('requisicao_id', requisicaoId)
    .order('changed_at', { ascending: false }); // Mais recente primeiro
  if (error) throw error;
  return data ?? [];
}

// Contar comentários por requisição (para exibir no painel)
export async function getComentariosCountByReqIds(requisicaoIds: string[]): Promise<Record<string, number>> {
  if (!requisicaoIds?.length) return {};
  // 1) Buscar todas as threads vinculadas às requisições em UMA query
  const { data: threads, error: eThreads } = await executeSupabaseQuery(
    getDbPublic()
    .from('app_comment_threads')
    .select('recurso_id,id')
    .eq('recurso', 'compras_requisicao')
    .in('recurso_id', requisicaoIds)
  );
  if (eThreads) throw eThreads;

  const threadIds = (threads ?? []).map((t: any) => t.id);
  if (!threadIds.length) return Object.fromEntries(requisicaoIds.map(id => [id, 0]));

  // 2) Trazer TODOS os comentários dessas threads em UMA query
  //    (somente a coluna 'thread_id' para reduzir payload)
  const { data: comments, error: eComments } = await executeSupabaseQuery(
    getDbPublic()
    .from('app_comments')
    .select('thread_id')
    .in('thread_id', threadIds)
    .is('deleted_at', null)
  );
  if (eComments) throw eComments;

  // 3) Agregar em memória: thread_id -> count
  const countByThread: Record<string, number> = {};
  for (const row of comments ?? []) {
    const tid = (row as any).thread_id;
    countByThread[tid] = (countByThread[tid] ?? 0) + 1;
  }

  // 4) Mapear para requisicao_id -> soma dos counts de suas threads
  const map: Record<string, number> = {};
  for (const th of threads ?? []) {
    const reqId = (th as any).recurso_id;
    const tid = (th as any).id;
    map[reqId] = (map[reqId] ?? 0) + (countByThread[tid] ?? 0);
  }
  // Garantir chaves com 0
  for (const id of requisicaoIds) if (map[id] == null) map[id] = 0;
  return map;
}

// =====================================================
// DECLARAÇÕES DE TIPO GLOBAIS
// =====================================================

export async function excluirRequisicao(id: string) {
  const { error } = await supabase.rpc('fn_requisicao_delete', { p_requisicao_id: id });
  if (error) {
    // loga o erro serializado para ver code/hint no console
    console.error("❌ excluirRequisicao RPC:", JSON.stringify(error, null, 2));
    throw error;
  }
}

// =====================================================
// COMENTÁRIOS COM EMBEDDING (padrão Tarefas)
// =====================================================

export async function listarComentariosPorThread(threadId: string) {
  const { data, error } = await supabase
    .schema('public')
    .from('app_comments')
    .select(`
      id, thread_id, conteudo, tipo, created_at, updated_at, editado, fixado, ordem_fixacao, deleted_at,
      created_by, updated_by,
      arquivo_url, arquivo_nome,
      criado_por:usuarios!app_comments_created_by_fkey ( id, nome, email ),
      atualizado_por:usuarios!app_comments_updated_by_fkey ( id, nome, email )
    `)
    .eq('thread_id', threadId)
    .is('deleted_at', null)
    .order('fixado', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

// -----------------------------------------
// Funções auxiliares para ComprasHome
// -----------------------------------------

export async function listarObrasCombo(): Promise<Array<{id: string; nome: string}>> {
  // ⚠️ ATENÇÃO: obras está em PUBLIC; use getDbPublic (não o client com schema 'acor')
  const { data, error } = await executeSupabaseQuery(
    getDbPublic()
    .from('obras')
    .select('id, nome')
    .in('status', ['em_andamento', 'paralisada', 'concluida'])
    .eq('is_personal', false)     // ✅ não listar obras pessoais
    .order('nome', { ascending: true })
  );

  if (error) throw error;
  return data ?? [];
}

/**
 * @deprecated Use criarRequisicaoAvancada ou criarRequisicaoVazia com finalidade_id obrigatório.
 * Esta função não permite especificar finalidade, que agora é obrigatória.
 */
export async function criarRequisicao(obraId: string, finalidadeId: string): Promise<string> {
  const id = await criarRequisicaoVazia(obraId, { finalidade_id: finalidadeId });
  if (!id) throw new Error('Falha ao criar requisição');
  return id;
}

// Declaração de tipo para window.comprasDebug
declare global {
  interface Window {
    comprasDebug: {
      peekRequisicaoItens: (reqId: string) => Promise<any[]>;
      peekHistorico: (materialId: string, janelaDias?: number) => Promise<any>;
      dumpComparativo: (reqId: string) => Promise<any>;
      peekTripla: (materialId: string) => Promise<Tripla | null>;
      peekHistoricoSemMarca: (materialId: string, dias?: number) => Promise<any>;
    };
  }
}

// -----------------------------------------
// Contador de itens solicitados com real-time
// -----------------------------------------

// --- Utils -------------------------------------------------------------------

async function getCurrentUsuariosId(): Promise<string | null> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const email = auth?.user?.email;
    if (!email) return null;

    const { data, error } = await supabase
      .from('usuarios')
      .select('id,email')
      .eq('email', email)
      .maybeSingle();

    if (error) return null;
    return data?.id ?? null;
  } catch {
    return null;
  }
}

type CountWhere = {
  usuarioId: string;
  status?: string; // default 'solicitado'
};

async function countWhere(table: string, where: CountWhere): Promise<number> {
  const status = where.status ?? 'solicitado';

  // Campos possíveis de vínculo com o usuário nas compras:
  const whoClauses = [
    `responsavel_id.eq.${where.usuarioId}`,
    `solicitante_id.eq.${where.usuarioId}`,
    `criado_por.eq.${where.usuarioId}`,
  ];

  const { count, error } = await executeSupabaseQuery(
    getDbAcor()
    .from(table)
    .select('id', { count: 'exact', head: true })
    .eq('status', status)
    .or(whoClauses.join(','))
  );

  if (error) throw error;
  return count ?? 0;
}

async function getSolicitadoCountInternal(usuarioId: string): Promise<number> {
  const tables = ['compras_pedidos', 'compras_requisicoes', 'compras'];
  for (const t of tables) {
    try {
      return await countWhere(t, { usuarioId, status: 'solicitado' });
    } catch {
      // tenta próxima tabela
    }
  }
  return 0;
}

// --- Hook --------------------------------------------------------------------

/**
 * Hook de contador realtime para "status = 'solicitado'" nas compras do usuário logado.
 * - Resolve usuarios.id (como nas notificações)
 * - Busca contagem inicial
 * - Assina eventos realtime e recalcula ao vivo
 */
export function useSolicitadoCount() {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const usuarioIdRef = useRef<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const uid = usuarioIdRef.current || (await getCurrentUsuariosId());
      usuarioIdRef.current = uid;
      if (!uid) {
        setCount(0);
        setLoading(false);
        return;
      }
      const c = await getSolicitadoCountInternal(uid);
      setCount(c);
    } catch (e: any) {
      setError(e?.message || 'Erro ao buscar contador de solicitados');
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Mount: resolve usuário e faz primeira contagem
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await refresh();

      // cria canal realtime apenas uma vez após resolver usuarioId
      const uid = usuarioIdRef.current;
      if (!uid) return;

      // Observa mudanças nas duas tabelas principais
      const chan = supabase.channel('compras-solicitado-realtime', {
        config: { broadcast: { ack: false }, presence: { key: uid } },
      });

      // Observa INSERT/UPDATE/DELETE e apenas recalcula
      const tables = ['compras_pedidos', 'compras_requisicoes'];
      tables.forEach((table) => {
        chan.on(
          'postgres_changes',
          { event: '*', schema: 'public', table },
          async () => {
            // Estratégia simples e segura: recalcular
            // (poderíamos otimizar validando status/ids no payload.new/old)
            try {
              const c = await getSolicitadoCountInternal(usuarioIdRef.current!);
              setCount(c);
            } catch (e) {
              // se falhar, mantém o último valor
              console.warn('⚠️ Realtime compras: falha ao recalcular', e);
            }
          }
        );
      });

      chan.subscribe(() => {
        // opcional: console.log('compras realtime status', status);
      });

      channelRef.current = chan;
    })();

    return () => {
      alive = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  return { count, loading, error, refresh };
}

// Função de compatibilidade para uso externo
export async function getSolicitadoCount(userId: string | null): Promise<number> {
  if (!userId) return 0;
  return await getSolicitadoCountInternal(userId);
}

// Comentário automático com itens da requisição (v2 - apenas no Salvar)
export async function comentarItensRequisicao(requisicaoId: string, authorId?: string | null): Promise<void> {
  // resolve autor de forma resiliente (pode ser null; a função trata via COALESCE)
  const uid =
    authorId ??
    (await supabase.auth.getUser().then(r => r.data.user?.id).catch(() => undefined)) ??
    null;

  const attempts: Array<{
    name: string;
    payload: Record<string, any>;
    disableKey?: 'rpc_v2_indisponivel' | 'rpc_core_indisponivel';
    skipIfDisabled?: boolean;
  }> = [
    { name: 'fn_compras_requisicao_comentar_itens_v2', payload: { p_requisicao: requisicaoId, p_author: uid }, disableKey: 'rpc_v2_indisponivel', skipIfDisabled: true },
    { name: 'fn_compras_requisicao_comentar_itens_v2_jwt', payload: { p_requisicao: requisicaoId }, disableKey: 'rpc_v2_indisponivel' },
    { name: 'fn_compras_requisicao_comentar_itens', payload: { p_requisicao_id: requisicaoId, p_author: uid } },
    { name: 'fn_compras_requisicao_comentar_itens_jwt', payload: { p_requisicao: requisicaoId } },
    { name: '_fn_app_compras_requisicao_comentar_itens_core', payload: { p_requisicao_id: requisicaoId, p_author: uid }, disableKey: 'rpc_core_indisponivel' },
    { name: '_fn_compras_requisicao_comentar_itens_core', payload: { p_requisicao_id: requisicaoId, p_author: uid }, disableKey: 'rpc_core_indisponivel' },
  ];

  for (const attempt of attempts) {
    if (attempt.skipIfDisabled && comentarItensRpcDisabled) {
      console.info('[comentarItensRequisicao] pulando', attempt.name, 'porque RPC v2 já está desabilitado', comentarItensRpcDisabled);
      continue;
    }
    try {
      const { data, error } = await supabase.rpc(attempt.name as any, attempt.payload);
      if (error) throw error;
      if (!data) throw new Error(`${attempt.name} retornou vazio`);
      console.info('[comentarItensRequisicao] sucesso via', attempt.name);
      return;
    } catch (e: any) {
      const message = (e?.message ?? '').toString().toLowerCase();
      const shouldDisable =
        e?.code === '42883' ||
        e?.code === '404' ||
        e?.status === 404 ||
        e?.code === '42501' ||
        message.includes('does not exist');

      console.warn(`[comentarItensRequisicao] ${attempt.name} falhou`, e);

      if (attempt.disableKey === 'rpc_v2_indisponivel' && shouldDisable) {
        disableComentarItensRpcAtRuntime('rpc_v2_indisponivel', e);
      }
      if (attempt.disableKey === 'rpc_core_indisponivel' && shouldDisable) {
        disableComentarItensRpcAtRuntime('rpc_core_indisponivel', e);
      }
    }
  }

  try {
    await comentarItensRequisicaoManual(requisicaoId, uid);
    return;
  } catch (manualError) {
    console.error('[comentarItensRequisicao] Todas as tentativas de comentar falharam, inclusive fallback manual.', manualError);
  }
}

// ensureThreadForRequisicao não é mais necessário: a RPC de comentar garante a thread.

